/**
 * The ball. (§3.2 aerodynamics, §3.4 ball-player contact, §4.2 passing, §4.3 shooting)
 *
 * "The ball is the star of the game. Model it properly." (§3.2) Everything a
 * football game lives or dies on for feel runs through this file:
 *
 *   updatePossession — who has the ball at their feet. The locomotion cap
 *     (MAX_SPEED_DRIBBLE) and the dribble touch model both branch on it, so it
 *     runs at pipeline stage 3, BEFORE player control (see step.ts).
 *
 *   resolveKicks — turns a held Pass/Shoot flag into an impulse + strike-offset
 *     spin (§3.2), resolves pass power/aim (§4.2) and shot mechanics (§4.3), and
 *     SNAPSHOTS THE OFFSIDE LINE at the exact kick tick (§5.4, D8). It runs at
 *     stage 6, before ball integration, so the snapshot captures positions at the
 *     instant the ball is played, not one tick late — ~14cm at sprint speed, the
 *     margin most offside calls turn on. It writes the snapshot; rules/ reads it.
 *
 *   stepBall — gravity, quadratic drag WITH the drag crisis (§3.2's Cd dip around
 *     the critical Reynolds number), the Magnus force (the soul of the game),
 *     spin decay, and bounce/roll ground contact through the PhysicsWorld.
 *
 * ── The strike-offset spin model (§3.2), stated once ────────────────────────
 *
 * Spin is NOT a fake "curve" stat. A kick is an impulse applied at a point offset
 * `d` from the ball's centre; the linear result is Δv = impulse/m and the spin is
 * Δω = (d × impulse)/I. Hit the ball low and it climbs with backspin; hit it off
 * to the side and it curls. We back-solve `d` from the intended spin and clamp it
 * to a fraction of the radius, which means a soft pass physically cannot be given
 * an elite curl — the geometry says so, no special case required.
 *
 * ── What this file may NOT do ───────────────────────────────────────────────
 *
 * No allocation per tick (§15.7): module-scope scratch and out-params only.
 * No Math.random (§15.3): state.rng via pcg32. No Math.sin/cos/atan2/pow: the
 * d* wrappers from math/vec3.js, and dpow025 (= sqrt(sqrt)) for the Magnus ^0.25,
 * which is bit-identical on every engine (DECISIONS.md D2). No wall clock: state.tick.
 */

import type { InputCommand, StepResult, WorldState } from './types.js';
import {
  AnimState,
  BallControl,
  InputFlags,
  MatchPhase,
  MAX_PLAYERS,
  SkillMove,
  emptyInput,
} from './types.js';
import {
  ActionKind,
  ActionPhase,
  actionDef,
  actionPhaseAt,
  normalizedActionTime,
  inContactWindow,
  isTackleKind,
  isTouchKind,
} from './contact-actions.js';
import { beginAnimAction } from './anim-exec.js';
import { ContactSocket, computeSocket, type SocketVec } from './contact-sockets.js';
import {
  ASSIST_CONE_DEG,
  BICYCLE_BACKSPIN,
  BICYCLE_BASE_ANGLE_DEG,
  BICYCLE_ERROR_MULTIPLIER,
  BICYCLE_MAX_FORWARD_DOT,
  BICYCLE_MIN_BALL_HEIGHT,
  BICYCLE_OVERHEAD_RADIUS,
  BICYCLE_POWER_MULTIPLIER,
  BICYCLE_REACH,
  BICYCLE_RECOVERY_S,
  BALL_AREA,
  BALL_CONTACT_SKIN,
  BALL_FRICTION_GRASS,
  BALL_GROUND_REST_SPEED,
  BALL_HEADREST_MAX_SPEED,
  BALL_HEADREST_MIN_Y,
  BALL_HEADREST_NY_MIN,
  BALL_HEADREST_ROT_RAD,
  BALL_HEADREST_SHOVE,
  BALL_INERTIA,
  BALL_MASS,
  BALL_MAX_ANGULAR_VELOCITY,
  BALL_RADIUS,
  BALL_RESTITUTION_GRASS,
  BALL_RESTITUTION_POST,
  BALL_ROLLING_RESISTANCE,
  BALL_SPIN_DECAY_AIR,
  BALL_SPIN_DECAY_GROUND,
  CD_HIGH_SPEED,
  CD_LAMINAR,
  CD_TURBULENT,
  CROSS_MAX_ANGLE_DEG,
  CROSS_MIN_ANGLE_DEG,
  CROSS_WIDE_FRAC,
  DRAG_CRISIS_MAX_SPEED,
  DRAG_LAMINAR_MAX_SPEED,
  DRAG_TURBULENT_MAX_SPEED,
  DRIVEN_PASS_BACKSPIN,
  DRIVEN_PASS_MAX_SPEED,
  DRIVEN_PASS_MIN_SPEED,
  FINESSE_POWER_MULTIPLIER,
  FINESSE_SIDESPIN,
  FIXED_DT,
  GRAVITY,
  GROUND_PASS_BACKSPIN,
  GROUND_PASS_MAX_SPEED,
  GROUND_PASS_MIN_SPEED,
  HEADER_BASE_ERROR_RAD,
  HEADER_BASE_SPEED,
  HEADER_CONTACT_HEIGHT,
  HEADER_DOWN_ANGLE_MAX_DEG,
  HEADER_DOWN_MIN_CONTACT_Y,
  HEADER_EXTRA_REACH,
  HEADER_VERTICAL_ERROR_DEG,
  KICK_ANIM_HOLD_S,
  HEADER_LIFT_ANGLE_MAX_DEG,
  HEADER_MAX_SPEED,
  HEADER_REDIRECT_FRACTION,
  DRIBBLE_GATHER_CLOSE_GAIN,
  DRIBBLE_GATHER_CLOSE_MAX_DV,
  DRIBBLE_GATHER_LEAD,
  DRIBBLE_GATHER_MAX_DIST,
  DRIBBLE_GATHER_MAX_REL_SPEED,
  DRIBBLE_GATHER_MIN_CARRY_SPEED,
  DRIBBLE_GATHER_RADIUS,
  DRIBBLE_GATHER_START,
  DRIBBLE_FOOT_OFFSET,
  DRIBBLE_GATHER_STRENGTH,
  DRIBBLE_KNOCK_LINE_RECOVER_MAX,
  DRIBBLE_KNOCK_LINE_RECOVER_S,
  DRIBBLE_KNOCK_LINE_TOL,
  DRIBBLE_KNOCK_MIN_SPEED,
  DRIBBLE_KNOCK_PROX,
  DRIBBLE_KNOCK_REL_SPEED,
  DRIBBLE_KNOCK_TRIGGER_REL,
  DRIBBLE_LEAD_DISTANCE,
  CLOSE_CONTROL_LEAD_MULT,
  DRIBBLE_LEAD_MAX,
  DRIBBLE_MAX_REL_SPEED,
  DRIBBLE_RECAPTURE_DAMP,
  DRIBBLE_RECAPTURE_GAIN,
  DRIBBLE_SETTLE_LINE_TOL,
  DRIBBLE_SETTLE_RADIUS,
  DRIBBLE_SETTLE_RADIUS_RUN,
  DRIBBLE_SETTLE_REL_SPEED,
  DRIBBLE_STEER_WEIGHT,
  DRIBBLE_WEAVE_HALF_PERIOD_S,
  DRIBBLE_WEAVE_MIN_SPEED,
  DRIBBLE_WEAVE_RAMP_SPEED,
  DRIBBLE_TOUCH_MAX_DV,
  DRIBBLE_TURN_SHEPHERD_DV,
  DRIBBLE_TURN_SHEPHERD_MAX_REL,
  DRIBBLE_TURN_RECOVER_S,
  DRIBBLE_TURN_SHEPHERD_TOL_DEG,
  DRIBBLE_TOUCH_PUSH,
  DRIBBLE_TOUCH_SPEED_FACTOR,
  LOFTED_PASS_MAX_ANGLE_DEG,
  CHIP_SHOT_ANGLE_DEG,
  LOFT_APEX_MIN_M,
  LOFT_APEX_MAX_M,
  LOFTED_PASS_MIN_ANGLE_DEG,
  MAGNUS_CL_COEFF,
  MAGNUS_CL_MAX,
  DRIVEN_PASS_CURL_FACTOR,
  MAX_SPEED_SPRINT,
  MAX_SPEED_SPRINT_DRIBBLE,
  MOUSE_CURL_SIDESPIN,
  NET_VELOCITY_DAMPING,
  PASS_CURL_POWER_FLOOR,
  PASS_ASSIST_CHARGE_HEADROOM,
  PASS_ASSIST_SPEED_BASE,
  PASS_ASSIST_SPEED_PER_M,
  PASS_BASE_ERROR_RAD,
  PASS_CHARGE_MAX_S,
  PASS_CHARGE_MIN_S,
  FIRST_TOUCH_RADIUS,
  FIRST_TOUCH_MAX_SPEED,
  FIRST_TOUCH_MIN_SPEED,
  FIRST_TOUCH_TARGET_REL_SPEED,
  FIRST_TOUCH_MAX_DV,
  FIRST_TOUCH_ALIGN_MIN,
  FIRST_TOUCH_COOLDOWN_TICKS,
  POSSESSION_CAPTURE_REL_SPEED,
  POSSESSION_CONTROL_RADIUS,
  POSSESSION_CONTROL_REL_SPEED,
  CHEST_CONTROL_MAX_HEIGHT,
  CHEST_CONTROL_MIN_HEIGHT,
  KNEE_CONTROL_DROP_SPEED,
  CONTROL_DEADEN,
  CHEST_CONTROL_DROP_SPEED,
  KNEE_CONTROL_MIN_HEIGHT,
  POSSESSION_MAX_HEIGHT,
  BALL_PLAY_REACH,
  STRIKE_OWN_BALL_REACH,
  POSSESSION_RADIUS,
  BALL_CONTEST_MAX_BALL_SPEED,
  BALL_CONTEST_MAX_TICKS,
  BALL_CONTEST_SCORE_MARGIN,
  BALL_CONTROL_W_FACING,
  BALL_CONTROL_W_NEAR,
  BALL_CONTROL_W_SETTLED,
  POSSESSION_STEAL_CLOSING_SPEED,
  POSSESSION_STEAL_FRONTAL_DOT,
  POSSESSION_STICKY_MARGIN,
  STEAL_CHALLENGE_RANGE,
  POWER_SHOT_MULTIPLIER,
  POWER_SHOT_SPIN_MULTIPLIER,
  BOOST2_KICK_POWER_MULTIPLIER,
  PRESSURE_ERROR_WEIGHT,
  PRESSURE_RADIUS,
  DEAD_BALL_OUT_MARGIN,
  DEAD_BALL_DAMP_PER_S,
  QUANT_POS_MAX_X,
  QUANT_POS_MAX_Y,
  QUANT_POS_MAX_Z,
  SHOT_ANGLE_MAX_DEG,
  SHOT_ANGLE_MIN_DEG,
  SHOT_BASE_ERROR_RAD,
  SHOT_CHARGE_MAX_S,
  SHOT_CHARGE_MIN_S,
  SHOT_LIGHT_CHARGE_S,
  SHOT_DISTANCE_ERROR_REF,
  SHOT_LIFT_ANGLE_MAX_DEG,
  SHOT_LIFT_SPIN,
  SHOT_MAX_SPEED,
  SHOT_VERTICAL_ERROR_DEG,
  PASS_LEAD_CONTROL_FOOT_STEP,
  PASS_LEAD_ITERS,
  PASS_LEAD_MAX_FRAC,
  PASS_VERTICAL_ERROR_DEG,
  POWER_SHOT_ERROR_MULTIPLIER,
  SHOT_MIN_SPEED,
  SHOT_OFFBALANCE_ERROR_WEIGHT,
  STRIKE_ARRIVING_CLOSING_SPEED,
  STRIKE_ARRIVING_MIN_FACING_DOT,
  STRIKE_FACING_MIN_DIST,
  STRIKE_MAX_HEIGHT,
  STRIKE_MIN_FACING_DOT,
  STRIKE_OFFSET_MAX_FRAC,
  THROUGH_BALL_LEAD_CAP_S,
  VOLLEY_ACCURACY_PENALTY,
  VOLLEY_HEIGHT_THRESHOLD,
  WET_FRICTION_MULTIPLIER,
  WET_RESTITUTION_MULTIPLIER,
  JUMP_GROUND_EPSILON,
} from './constants.js';
import type { PhysicsQueryResult, PhysicsWorld } from './physics.js';
import { ContactKind, pitchBounds } from './physics.js';
import {
  angleDelta,
  DEG2RAD,
  clamp,
  clamp01,
  datan2,
  dcos,
  dpow025,
  dsin,
  wrapAngle,
} from '../math/vec3.js';
import { pcg32GaussianClamped } from '../math/pcg32.js';
import { statMultiplier, resolveContactTackle } from './player-controller.js';
import { evaluateOffsidePositions } from './rules/index.js';
import {
  BallVelocityCause,
  ballTraceOn,
  traceBallDv,
  traceBallSince,
} from './ball-trace.js';
import { footContact, inFootReach } from './foot-contact.js';
import { contactLogOn, logContact } from './contact-log.js';
import { getPossessionTrace, PossessionChangeReason, PossessionState } from './possession-trace.js';
import { choosePassTarget } from './pass-target.js';
import { createBallTravel, travelForDistance } from './ground-travel.js';

// ===========================================================================
// Module-scope scratch. Never allocate in the tick. (§15.7)
// ===========================================================================

/** out-param for physics.sweepBall — reused every tick. */
const sweepOut: PhysicsQueryResult = {
  hit: false, playerId: -1, kind: ContactKind.None, toi: 1,
  nx: 0, ny: 0, nz: 0, px: 0, py: 0, pz: 0,
};

/** Result of resolving a kick's launch: filled by buildKick(), read by applyStrike(). */
const kick = {
  /** Target linear velocity of the ball after the strike. */
  vx: 0, vy: 0, vz: 0,
  /** Intended spin, rad/s, split into a back/topspin magnitude and a sidespin. */
  backspin: 0, // + = backspin (float/lift), − = topspin (dip)
  sidespin: 0, // signed; sign is the curl direction
  /** Was this strike a "power kick" (boost tier 2, or full charge)? Every
   *  builder below MUST set this explicitly (never leave it at a previous
   *  kick's value) — see BallState.powerKick and applyStrike, which copies
   *  it across. */
  powerKick: false,
};

// ===========================================================================
// Small helpers — all allocation-free.
// ===========================================================================

/** Map a physics ContactKind to the ball-trace cause for that surface. (§3.2) */
function surfaceCause(kindTag: number): BallVelocityCause {
  switch (kindTag) {
    case ContactKind.Post: return BallVelocityCause.PostContact;
    case ContactKind.Crossbar: return BallVelocityCause.CrossbarContact;
    case ContactKind.Net: return BallVelocityCause.PostContact; // goal-frame surface
    case ContactKind.Player: return BallVelocityCause.PlayerBodyContact;
    // Wall (world-edge containment) folds into GroundContact until Step 6
    // removes the walls entirely.
    default: return BallVelocityCause.GroundContact;
  }
}

/** Horizontal (XZ) distance. Players and the ball at feet live on a plane. */
function distXZ(ax: number, az: number, bx: number, bz: number): number {
  const dx = ax - bx;
  const dz = az - bz;
  return Math.sqrt(dx * dx + dz * dz);
}

/** Attack direction on the X axis for `team`: +1 attacks +X, −1 attacks −X. (§9.2) */
function attackDirX(state: WorldState, team: number): number {
  return ((team === 0) === state.teamAAttacksPositiveX) ? 1 : -1;
}

// ===========================================================================
// updatePossession (§3.4) — pipeline stage 3
// ===========================================================================

/**
 * Decide who, if anyone, has the ball at their feet, and write it into
 * ball.possessedBy + players.possessing[]. The locomotion cap and the dribble
 * touch model both read those flags, so this MUST run before player control.
 *
 * A player possesses the ball when it is (1) within POSSESSION_RADIUS, (2) below
 * reach height, and (3) slow RELATIVE to them. The relative-speed gate is the
 * important one: without it a 20 m/s shot would be "re-possessed" by the striker
 * on the next tick because the ball is briefly still within 1.2m of his foot.
 * Requiring the ball to move WITH the player is what makes possession mean
 * control rather than mere proximity — and it makes speed-dribbling risky for
 * free, because a heavy touch outruns the gate and possession drops until the ball
 * settles.
 *
 * A keeper with the ball in his gloves is left untouched: he re-asserts the hold
 * at stage 5, and clearing it here would open a one-tick window for an opponent to
 * "intercept" a held ball (player-controller's tryIntercept only bails on
 * possessedBy !== -1).
 */
/**
 * How strong is this player's claim on the ball right now? (Phase-1 Commit 2)
 *
 * Three terms, in the order they actually matter:
 *   1. RELATIVE SPEED. `updatePossession` already treats this as the thing
 *      that separates control from proximity, and it is the same physical
 *      statement here: a ball moving fast in your own frame is one you cannot
 *      take a touch on, however close it is.
 *   2. DISTANCE, normalised over the capture radius.
 *   3. FACING. You cannot control what you are not looking at — the same
 *      reason tryIntercept gates on an awareness cone.
 *
 * Returns roughly 0..1. Used to rank rival claims, never to grant one: a claim
 * still has to pass every gate in `updatePossession` before it gets here.
 */
function controlScore(state: WorldState, id: number): number {
  const p = state.players;
  const b = state.ball;
  const d = distXZ(p.px[id]!, p.pz[id]!, b.px, b.pz);
  const rel = Math.sqrt(
    (b.vx - p.vx[id]!) * (b.vx - p.vx[id]!) + (b.vz - p.vz[id]!) * (b.vz - p.vz[id]!),
  );
  const near = 1 - clamp01(d / POSSESSION_RADIUS);
  const settled = 1 - clamp01(rel / POSSESSION_CAPTURE_REL_SPEED);
  const toBall = datan2(b.pz - p.pz[id]!, b.px - p.px[id]!);
  const facing = (dcos(angleDelta(p.yaw[id]!, toBall)) + 1) * 0.5;
  return near * BALL_CONTROL_W_NEAR
    + settled * BALL_CONTROL_W_SETTLED
    + facing * BALL_CONTROL_W_FACING;
}

export function updatePossession(state: WorldState): void {
  const p = state.players;
  const b = state.ball;

  // Respect a keeper's hold — see the header note.
  if (b.possessedBy >= 0 && p.gkHoldStartTick[b.possessedBy]! >= 0) return;

  const prev = b.possessedBy;
  let best = -1;
  let bestDist = Number.POSITIVE_INFINITY;

  if (b.py <= POSSESSION_MAX_HEIGHT) {
    for (let i = 0; i < MAX_PLAYERS; i++) {
      if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;

      const d = distXZ(p.px[i]!, p.pz[i]!, b.px, b.pz);
      if (d > POSSESSION_RADIUS) continue;

      // The ball must be moving with the player, not away from him.
      const rvx = b.vx - p.vx[i]!;
      const rvz = b.vz - p.vz[i]!;
      const relSpeed = Math.sqrt(rvx * rvx + rvz * rvz);
      if (relSpeed > POSSESSION_CAPTURE_REL_SPEED) continue;

      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
  }

  // Hysteresis: the incumbent keeps the ball unless a challenger is clearly
  // closer, so possession does not chatter between two players at the same range.
  if (
    best !== prev && prev >= 0 &&
    p.active[prev] === 1 && p.sentOff[prev] !== 1 &&
    b.py <= POSSESSION_MAX_HEIGHT
  ) {
    const dPrev = distXZ(p.px[prev]!, p.pz[prev]!, b.px, b.pz);
    if (dPrev <= POSSESSION_RADIUS && dPrev <= bestDist + POSSESSION_STICKY_MARGIN) {
      const rvx = b.vx - p.vx[prev]!;
      const rvz = b.vz - p.vz[prev]!;
      if (Math.sqrt(rvx * rvx + rvz * rvz) <= POSSESSION_CAPTURE_REL_SPEED) {
        best = prev;
      }
    }
  }

  // §1 CONTROLLED envelope — a knock/turn briefly runs the ball past the tight
  // capture gate while it is still the carrier's (the GATHER touch hard-caps the
  // distance, so this never runs away). Keep it with him inside the wider control
  // envelope — but ONLY when no VALID opponent challenge just won it (a real
  // interception/tackle, §14), so a defender can still take it and this only
  // closes the "separates with no kick and no tackle" bug.
  //
  // The incumbent is the last possessor, or — through the brief knock-and-run
  // blip where that already fell to -1 for a tick or two — the GATHER owner (the
  // ball is loose but still his). Without that fallback a hard turn parks the
  // ball at the envelope edge, just outside the tight capture gate, and it stays
  // "loose" for a second even though nobody challenged. A real KICK clears BOTH
  // possessedBy and gatherOwner in resolveKicks, so the incumbent is -1 and this
  // never re-grabs a ball the carrier genuinely played.
  //
  // Only in LIVE play: during a restart the ball is placed and owned by the rules
  // layer, and gatherOwner may still name a player from before the stoppage — the
  // wide keep must not re-grab a ball that is being set up or thrown/kicked in.
  const incumbent = prev >= 0 ? prev : b.gatherOwner;
  const bestIsOpponentOfIncumbent = best >= 0 && incumbent >= 0 && p.team[best]! !== p.team[incumbent]!;
  if (
    state.match.phase === MatchPhase.InPlay &&
    best !== incumbent && incumbent >= 0 && !bestIsOpponentOfIncumbent &&
    p.active[incumbent] === 1 && p.sentOff[incumbent] !== 1 && b.py <= POSSESSION_MAX_HEIGHT
  ) {
    const dInc = distXZ(p.px[incumbent]!, p.pz[incumbent]!, b.px, b.pz);
    const rvx = b.vx - p.vx[incumbent]!;
    const rvz = b.vz - p.vz[incumbent]!;
    if (dInc <= POSSESSION_CONTROL_RADIUS && Math.sqrt(rvx * rvx + rvz * rvz) <= POSSESSION_CONTROL_REL_SPEED) {
      best = incumbent;
    }
  }

  // §14 — a defender may not WREST the ball off a CONTROLLING opponent by mere
  // proximity/body contact. A rear or side brush on a shielding carrier does not
  // dispossess him; only a slide, a standing tackle, or a genuine FRONTAL
  // interception (the challenger inside the carrier's front arc, cutting the ball
  // off) counts.
  //
  // The protected party is the INCUMBENT — the last possessor, or (through the
  // knock-and-run blip where possessedBy briefly fell to -1) the GATHER owner: the
  // ball is loose for a tick or two but still his dribble. "Controlling" is the
  // wider CONTROL envelope (POSSESSION_CONTROL_RADIUS / _REL_SPEED), NOT just the
  // tight capture gate. This closes the live "possession flips on a brush" bug: a
  // heavy touch runs the ball into the 1.2–2.35 m envelope, the carrier is >1.2 m
  // from it for a moment, and under the old prev-only + tight-gate check a defender
  // stepping into that bubble from BEHIND took it by pure proximity. A real KICK
  // clears BOTH possessedBy and gatherOwner (applyStrike), and a landed tackle now
  // clears gatherOwner too (pokeBall / hookBall), so a genuinely played-or-lost
  // ball has no incumbent here and stays an open contest — the shield only covers
  // a ball the carrier is still actively dribbling.
  let approachDot = 0;
  const ballSpeedBefore = Math.sqrt(b.vx * b.vx + b.vz * b.vz);
  const guard = prev >= 0 ? prev : b.gatherOwner;
  if (
    best >= 0 && guard >= 0 && best !== guard &&
    p.team[best]! !== p.team[guard]! &&
    p.active[guard] === 1 && p.sentOff[guard] !== 1 &&
    b.py <= POSSESSION_MAX_HEIGHT
  ) {
    const ax = p.px[best]! - p.px[guard]!;
    const az = p.pz[best]! - p.pz[guard]!;
    const alen = Math.sqrt(ax * ax + az * az) || 1;
    approachDot = (dcos(p.yaw[guard]!) * ax + dsin(p.yaw[guard]!) * az) / alen;
    const dGuard = distXZ(p.px[guard]!, p.pz[guard]!, b.px, b.pz);
    const rvx = b.vx - p.vx[guard]!;
    const rvz = b.vz - p.vz[guard]!;
    // Use the TIGHT capture thresholds, not the wide control envelope: the shield
    // only covers a ball the carrier genuinely has at his feet (incl. through the
    // possessedBy=-1 knock blip via the gather owner). A ball that has drifted out
    // to the 1.2–2.35 m envelope is a fair loose-ball contest — protecting THAT too
    // makes possession so sticky the AI match measurably stops circulating the ball
    // (support-passing acceptance). Fixing the brush-steal must not freeze play.
    // ── WHAT COUNTS AS "STILL HIS" (Phase-1 Commit 1) ────────────────────
    //
    // This used the TIGHT capture gate (1.2 m), and that is where the passive
    // steal actually lived — removing the frontal-dot clause alone changed
    // NOTHING (12/12 steals before and after, measured). A sprinting dribbler
    // leads the ball up to DRIBBLE_LEAD_MAX ahead of himself, so for most of
    // every stride the ball is 1.2-2.3 m in front of him: outside this gate,
    // shield not applied, and the nearest opponent took it on `bestDist` with
    // no challenge whatsoever. The dot product was never reached.
    //
    // "Still his" is the CONTROL envelope — where the sim already says a ball
    // is under a carrier's control, and the same envelope the gather touch
    // works to. A ball inside it, travelling with him, is a dribble; taking it
    // requires a challenge. A ball that has genuinely escaped (outside the
    // envelope, or moving fast relative to him) is loose and free to contest.
    //
    // ⚠ The comment that used to sit here warned that widening this made
    // possession sticky enough to stop the AI circulating the ball. That was
    // measured when a defender's only route in was proximity; it is re-checked
    // against the support-passing and possession acceptance suites on every
    // change to these two constants, because the failure mode is real even if
    // the cause has moved.
    const guardControls =
      dGuard <= POSSESSION_CONTROL_RADIUS &&
      Math.sqrt(rvx * rvx + rvz * rvz) <= POSSESSION_CONTROL_REL_SPEED;
    // ── CAN CONTROL ≠ CAN STEAL (owner, Phase-1 Commit 1) ────────────────
    //
    // A frontal position used to be sufficient on its own: `approachDot >
    // POSSESSION_STEAL_FRONTAL_DOT`, on the theory that a challenger inside
    // the carrier's front arc was "cutting the ball off". Standing perfectly
    // still in front of a dribbler satisfies that dot product, so a defender
    // who pressed NOTHING was handed the ball the moment it entered his
    // acquisition cylinder — 12 out of 12 on the duel probe, no tackle, no
    // intercept, no input at all.
    //
    // The missing half is MOTION. A defender genuinely cutting a ball off is
    // moving at it; a passive body is not, and the ball arrives at him. So the
    // frontal arc is now a conjunct rather than a licence, paired with the
    // defender's own closing speed along the line to the ball. That keeps the
    // real frontal interception (possession-steal.test.ts drives the defender
    // straight at the ball, and it must keep winning it) and removes the
    // stationary case, which is exactly the owner's rule: ordinary proximity
    // must not transfer ownership, an actual ball interaction must.
    //
    // ⚠ `tryIntercept` deliberately bails on `possessedBy !== -1`, so it is
    // NOT a second route in — it acts on loose balls only. The routes to a
    // CONTROLLED ball are: challenge it, close on it, or make it loose first.
    const ca = p.anim[best]! as number;
    const bdx = b.px - p.px[best]!;
    const bdz = b.pz - p.pz[best]!;
    const blen = Math.sqrt(bdx * bdx + bdz * bdz) || 1;
    const closingSpeed = (p.vx[best]! * bdx + p.vz[best]! * bdz) / blen;
    const validSteal =
      ca === AnimState.SlideTackle || ca === AnimState.SlideRecover ||
      ca === AnimState.StandingTackle ||
      (approachDot > POSSESSION_STEAL_FRONTAL_DOT &&
       closingSpeed > POSSESSION_STEAL_CLOSING_SPEED);
    if (guardControls && !validSteal) {
      const tr = getPossessionTrace();
      if (tr !== null) {
        tr.recordRejectedSteal({ tick: state.tick, incumbent: guard, challenger: best, approachDot });
      }
      best = guard; // shielded — the carrier keeps his dribble
    }
  }

  // ── CONTEST (Phase-1 Commit 2) ─────────────────────────────────────────
  //
  // Acceptance test G: "neither gains magical immediate control purely because
  // his centre is 3 cm closer." Until now `bestDist` did exactly that — the
  // probe's 50/50 spends 14 ticks with both players inside POSSESSION_RADIUS
  // and resolved every one of them on a distance comparison.
  //
  // A CONTROL score ranks a claim on more than proximity: how near the ball
  // is, how slowly it is moving in that player's own frame (the thing that
  // actually decides whether he can take a touch), and whether he is looking
  // at it. When the two best claims belong to OPPONENTS and are within
  // BALL_CONTEST_SCORE_MARGIN of each other, neither has won it and the ball
  // stays loose — a real contest, visible to anything that wants to reason
  // about one.
  //
  // It is a countdown to a decision, not a stalemate: a contest that runs past
  // BALL_CONTEST_MAX_TICKS is resolved to the better score, so a ball can
  // never be frozen between two players. Team-mates never contest each other —
  // two players from the same side arriving together is not a duel, and making
  // it one would stall build-up play.
  //
  // ⚠ ONLY A LOOSE BALL IS CONTESTED. The first cut ran this on every tick and
  // broke five suites at once: a carrier in full control, brushed by a
  // defender the shield had already rejected, was scored against that defender
  // and the ball went "contested" — i.e. he dropped it, for four tenths of a
  // second, to a challenge that never happened. That is the very bug Commit 1
  // removed, wearing a different hat. The chain is FREE → CONTESTED →
  // CONTROLLED; a CONTROLLED ball becomes loose through a challenge first, and
  // only then is there anything to fight over.
  let contested = false;
  // `prev < 0` is not enough on its own: through the knock-and-run blip a
  // carrier's own possessedBy falls to -1 for a tick or two while the ball is
  // still unmistakably his dribble, and contesting THAT is the brush-steal
  // again (measured: the side-brush guard went red immediately). The gather
  // owner is the sim's existing answer to "whose ball is this really", so a
  // ball with an owner of either kind is not up for grabs.
  //
  // ⚠ AND ONLY A BALL SOMEBODY COULD ACTUALLY TAKE. A contest means two players
  // who could each control it; a ball travelling past them is not a 50/50, it
  // is a loose ball in transit, and holding it ownerless for the contest window
  // means `applyDribbleTouch` never runs and nothing shepherds it. MEASURED on
  // the 5-minute telemetry: without this gate a seed-42 match produced 88
  // goal-line restarts against a baseline of 1, one whistle every 3.4 seconds,
  // and animation flaps went 577 -> 1355 — the flapping was 22 players being
  // re-staged for a restart, not a clip problem.
  const contestBallSpeed = Math.sqrt(b.vx * b.vx + b.vz * b.vz);
  if (
    prev < 0 && b.gatherOwner < 0 && best >= 0 &&
    b.py <= POSSESSION_MAX_HEIGHT &&
    contestBallSpeed <= BALL_CONTEST_MAX_BALL_SPEED
  ) {
    const bestScore = controlScore(state, best);
    let rivalScore = -Infinity;
    for (let i = 0; i < MAX_PLAYERS; i++) {
      if (i === best || p.active[i] !== 1 || p.sentOff[i] === 1) continue;
      if (p.team[i]! === p.team[best]!) continue;
      if (distXZ(p.px[i]!, p.pz[i]!, b.px, b.pz) > POSSESSION_RADIUS) continue;
      const sc = controlScore(state, i);
      if (sc > rivalScore) rivalScore = sc;
    }
    if (rivalScore > -Infinity && bestScore - rivalScore < BALL_CONTEST_SCORE_MARGIN) {
      // ⚠ A CONTEST ONLY EVER DELAYS A DECISION — IT NEVER REASSIGNS ONE.
      //
      // The first cut resolved a timed-out contest to whichever rival had the
      // better control score. That quietly re-opened the passive steal Commit
      // 1 had just closed: `best` has already been through the shield, and the
      // rival has not, so handing him the ball on a timeout let a defender the
      // shield rejected collect it four tenths of a second later instead.
      // MEASURED: passive steals went 0/12 back to 2/12 the moment this
      // branch existed. The contest holds the ball loose while the fight is
      // genuinely open, and when the clock runs out the LEGAL claim takes it.
      if (b.contestTicks < BALL_CONTEST_MAX_TICKS) {
        contested = true;
        best = -1;
      }
    }
  }
  b.contestTicks = contested ? b.contestTicks + 1 : 0;
  b.controlState = contested
    ? BallControl.Contested
    : best >= 0 ? BallControl.Controlled : BallControl.Free;

  for (let i = 0; i < MAX_PLAYERS; i++) p.possessing[i] = i === best ? 1 : 0;
  b.possessedBy = best;
  // §1 GATHER owner: whoever is in control owns the gather. On a knock-and-run the
  // ball briefly runs past the capture gate (best flips to -1 for a few ticks) —
  // we do NOT clear the owner then, so the gather touch keeps bringing it back.
  // A rival winning it reassigns the owner (best >= 0); a KICK clears it in
  // applyStrike; a genuinely lost ball just leaves it stale (the gather is
  // distance-bounded, so a stale owner far from the ball never retrieves it).
  if (best >= 0) b.gatherOwner = best;

  // §14 diagnostics: attribute every genuine possession change to a reason code.
  if (best !== prev && best >= 0) {
    const tr = getPossessionTrace();
    if (tr !== null) {
      tr.recordChange({
        tick: state.tick,
        prev,
        next: best,
        reason: classifyPossessionChange(state, prev, best, ballSpeedBefore),
        approachDot: prev >= 0 ? approachDot : 0,
        ballSpeedBefore,
        ballSpeedAfter: Math.sqrt(b.vx * b.vx + b.vz * b.vz),
      });
    }
  }
}

/** Diagnostic speed (m/s) above which a newly-loose ball counts as a travelling
 *  (kicked) ball for the pass-interception label rather than a settled loose one.
 *  Classification only — never read by the sim. */
const POSSESSION_TRAVELLING_BALL_SPEED = 4.0;

/** §14 — attribute a possession change to one of the allowed reasons. Pure
 *  read-only classification for the diagnostic trace; does not affect the sim. */
function classifyPossessionChange(
  state: WorldState,
  prev: number,
  next: number,
  ballSpeedBefore: number,
): PossessionChangeReason {
  const p = state.players;
  if (state.positions[next] === 'GK') return PossessionChangeReason.GoalkeeperCollection;
  if (state.match.phase !== MatchPhase.InPlay) return PossessionChangeReason.RestartAssignment;
  if (prev < 0) {
    // Loose ball. A fast, travelling ball taken by the OTHER team is an intercept.
    if (
      ballSpeedBefore > POSSESSION_TRAVELLING_BALL_SPEED &&
      state.ball.lastTouchedTeam >= 0 &&
      state.ball.lastTouchedTeam !== p.team[next]!
    ) {
      return PossessionChangeReason.PassInterception;
    }
    return PossessionChangeReason.LooseBallControl;
  }
  // Off a controlling player.
  if (p.team[next]! !== p.team[prev]!) {
    const ca = p.anim[next]! as number;
    if (ca === AnimState.SlideTackle || ca === AnimState.SlideRecover) {
      return PossessionChangeReason.ValidSlideContact;
    }
    if (ca === AnimState.StandingTackle) {
      return PossessionChangeReason.ValidStandingTackle;
    }
    // The gate above only lets a frontal challenge through otherwise.
    return PossessionChangeReason.FrontalInterception;
  }
  // A teammate picked up a loose touch / give.
  return PossessionChangeReason.LooseBallControl;
}

/**
 * Classify the ball's current possession phase (§14 / Phase-1 "clear possession
 * states"). PURELY DERIVED from WorldState — nothing here is stored or hashed, so
 * it adds no determinism surface; the gameplay debug overlay calls it each frame
 * and tests read it as a stable "who, if anyone, owns the ball" summary.
 * Precedence: out of live play → GK hold → controlled (CONTESTED if an opponent
 * is inside challenge range) → airborne/travelling in-flight → settled loose.
 */
export function classifyPossessionState(state: WorldState): PossessionState {
  const b = state.ball;
  const p = state.players;
  if (state.match.phase !== MatchPhase.InPlay) return PossessionState.OutOfPlay;
  const holder = b.possessedBy;
  if (holder >= 0) {
    if (p.gkHoldStartTick[holder]! >= 0) return PossessionState.GoalkeeperControlled;
    return opponentWithinChallenge(state, holder)
      ? PossessionState.Contested
      : PossessionState.Controlled;
  }
  const speed = Math.sqrt(b.vx * b.vx + b.vz * b.vz);
  if (b.py > POSSESSION_MAX_HEIGHT || speed > POSSESSION_TRAVELLING_BALL_SPEED) {
    return PossessionState.InFlight;
  }
  return PossessionState.Loose;
}

/** True when an opponent of `holder` is inside challenge range of the ball — the
 *  "a tackle is imminent / underway" signal that makes a controlled ball
 *  CONTESTED rather than cleanly Controlled. */
/**
 * WHICH FOOT PASSES. The inside-of-the-boot push crosses the body: a ball
 * played to the player's LEFT is swept by the RIGHT foot and vice versa
 * (owner, 2026-08-14). Same side convention as `locomotionAnim`'s strafes:
 * `angleDelta(yaw, dir) > 0` is the player's LEFT. Centre (and left) keep the
 * right foot - the game's default footedness - so a straight ball never coin
 * flips between mirrored clips on floating-point noise.
 */
function passFootAnim(yaw: number, aimYaw: number): AnimState {
  return angleDelta(yaw, aimYaw) >= 0 ? AnimState.Pass : AnimState.PassLeftFooted;
}

function opponentWithinChallenge(state: WorldState, holder: number): boolean {
  const p = state.players;
  const b = state.ball;
  const holderTeam = p.team[holder]!;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1 || p.team[i]! === holderTeam) continue;
    if (distXZ(p.px[i]!, p.pz[i]!, b.px, b.pz) <= STEAL_CHALLENGE_RANGE) return true;
  }
  return false;
}

// ===========================================================================
// resolveKicks (§3.2 strike spin, §4.2 passing, §4.3 shooting, §5.4 offside) — stage 6
// ===========================================================================

/**
 * Apply this tick's kick, if one is thrown, and snapshot the offside line.
 *
 * At most ONE player strikes the ball per tick — a ball can only be hit once at
 * an instant. The striker is the player in possession who pressed Pass/Shoot; if
 * nobody possesses it, the nearest player within reach who pressed a kick may
 * still strike a loose or airborne ball (a volley, a first-time clearance).
 */

/**
 * Can this player plant a support foot RIGHT NOW?
 *
 * An ordinary footed pass or shot is a GROUND action: it needs a foot on the
 * pitch to push against. The sim used to let one start at any height, so a
 * player could strike a ground shot at the apex of a jump -- no plant, no
 * weight transfer, and the ground-kick swing playing in mid-air.
 *
 * Deliberately NOT keyed off the animation name (an anim can lag or be held
 * for follow-through). It reads the physical state: height above the pitch and
 * vertical velocity. `JUMP_GROUND_EPSILON` is the same band the jump gate uses,
 * so "grounded enough to jump" and "grounded enough to kick" agree.
 *
 * Inside the band a FAST descent is still airborne: it is the last tick before
 * touchdown and a ground shot fired there is visibly mid-air. A player merely
 * settled on the pitch can carry a few cm/s of residual vy from the integrator,
 * which is not a fall -- hence a tolerance rather than `vy >= 0`.
 *
 * Headers and bicycle kicks are aerial actions with their own eligibility and
 * do NOT come through here.
 */
export function canPlantSupportFoot(state: WorldState, id: number): boolean {
  const p = state.players;
  // Off the pitch, or still descending onto it.
  if (p.py[id]! > JUMP_GROUND_EPSILON) return false;
  if (p.vy[id]! < -GROUND_ACTION_MAX_DESCENT) return false;
  // ON the floor is not the same as ON YOUR FEET. A knocked-down player is
  // planted with py 0 and vy 0, so the height test alone happily lets him pass
  // the ball while lying on the grass; knockdownTimer also covers the bicycle
  // landing and the get-up that follows it. A stumble is the mild version of
  // the same thing -- off balance, no weight to transfer through a plant.
  if (p.knockdownTimer[id]! > 0) return false;
  if (p.stumbleTimer[id]! > 0) return false;
  // Mid-slide the legs are committed and there is no support leg at all. The
  // slide's own recovery runs on knockdownTimer, caught above.
  if (isTackleKind(p.actKind[id]! as ActionKind)) return false;
  return true;
}

/** Downward speed (m/s) above which a player inside the ground band is treated
 *  as still falling. A settled player carries only integrator residue; a real
 *  fall from a jump arrives several times faster. */
const GROUND_ACTION_MAX_DESCENT = 0.5;

export function resolveKicks(
  state: WorldState,
  inputs: ReadonlyArray<InputCommand | null>,
  out: StepResult,
): void {
  const b = state.ball;

  // Already played this tick (a keeper distributed at stage 5, or a tackle/
  // interception poked it at stage 4). Do not strike the same ball twice.
  if (b.lastTouchTick === state.tick) return;

  // §2 CONTACT-TIMED path (Training only for now): advance any in-flight action so
  // its stored strike fires at the animation contact frame. Competitive play has
  // contactTiming=false and skips this entirely (byte-identical).
  if (state.contactTiming) {
    advanceContactActions(state, out);
    if (b.lastTouchTick === state.tick) return; // an action struck this tick
  }

  // ── Bicycle kick — checked FIRST, and it wins when legal. (house rule) ─────
  // The BicycleKick flag is a REQUEST composed client-side (B+Space+Shoot); the
  // sim is the legality authority. A legal request takes precedence over an
  // ordinary shot and fires the overhead scissor. An ILLEGAL request simply
  // falls through to the normal path below — so a normal Shoot from the same
  // input still fires if it would have been legal, and an impossible overhead
  // position fires nothing (findStriker won't reach a ball that high/far).
  const biker = findBicycleStriker(state, inputs);
  if (biker >= 0) {
    const bikeInput = inputs[biker]!;
    evaluateOffsidePositions(state, state.players.team[biker]!, biker, state.match.restartKickPhase, state.offside);
    state.match.restartKickPhase = MatchPhase.InPlay; // restart taken; consumed
    buildBicycle(state, biker, bikeInput);
    applyStrike(state, biker, 'bicycle');
    startBicycleRecovery(state, biker);
    return;
  }

  const striker = findStriker(state, inputs);
  if (striker < 0) return;

  const input = inputs[striker]!;
  const shooting = (input.flags & InputFlags.Shoot) !== 0;

  // AIRBORNE GROUND-ACTION BLOCK. A footed strike at or below head height is an
  // ordinary GROUND pass/shot and requires a plantable support foot. Bail out
  // entirely when there is none: no ball impulse, no ground-kick animation, and
  // no armed contact action left behind to fire on a later tick. The input is
  // simply not consumed, so it takes effect on landing if still held.
  // A ball above HEADER_CONTACT_HEIGHT is a header -- an aerial action with its
  // own rules -- and is unaffected. Bicycles return before this point.
  if (b.py <= HEADER_CONTACT_HEIGHT && !canPlantSupportFoot(state, striker)) return;

  // §2 contact-timing: a GROUND footed Shoot/Pass starts an ACTION whose impulse
  // fires later, at the contact frame — the ball does NOT move on this input tick.
  // (Header / air / bicycle stay on the immediate path even under contact-timing;
  // air actions are out of scope this pass.) The offside snapshot moves to the
  // actual strike inside resolveActionStrike.
  // A DribbleTouch/FirstTouch is almost ALWAYS in flight while carrying the ball
  // (DribbleTouch is 8 ticks and re-arms continuously), so requiring None here
  // meant every shot taken while MOVING fell through to the immediate path
  // below: the ball left on the input tick and the swing played afterwards, into
  // empty air. Measured — strike at action phase 0.042 against a 0.35–0.50
  // window, with the boot 0.82m from the ball. A strike outranks a touch, so it
  // PREEMPTS one; a strike already in flight still wins over a new request.
  // ⚠ HEADERS GO THROUGH CONTACT TIMING TOO (owner: "when players hits the
  // ball with head its glitches physics not working on it"). This condition
  // was `b.py <= HEADER_CONTACT_HEIGHT`, which EXCLUDED headers and sent them
  // down the immediate path: the impulse landed on the input tick and the
  // Header clip played afterwards, so the ball was already gone by the time
  // the head swung. Same defect the comment above describes for moving shots,
  // and it was knowingly deferred ("air actions are out of scope this pass").
  const inFlight = state.players.actKind[striker]! as ActionKind;
  if (state.contactTiming) {
    if (inFlight === ActionKind.None || isTouchKind(inFlight)) {
      startContactAction(state, striker, input, shooting);
    }
    // Either we just armed the swing, or an action already owns this player's
    // contact. EITHER WAY the ball must not move now — the impulse fires at the
    // contact frame in advanceContactActions.
    //
    // Returning here is the whole point. The shoot button is held across several
    // ticks, so the tick AFTER arming re-entered resolveKicks with a NormalShot
    // in flight, fell past this guard, and struck the ball immediately on the
    // legacy path below: measured contact at action phase 0.042 against a
    // 0.35-0.50 window, boot 0.82m from the ball, swing playing into empty air
    // afterwards.
    return;
  }

  // Snapshot the offside line NOW, before the strike moves the ball, at these
  // exact positions (§5.4, D8). evaluateOffsidePositions is the rules module's
  // source of truth for who is in an offside POSITION; ball.ts owns the call site
  // and the rules evaluator later reads what we write. fromRestart is normally
  // InPlay, but when the FSM has just staged a restart Live (match.restartKickPhase
  // = Corner/GoalKick/…), THIS is the restart kick: tag the snapshot with it so
  // the Law-11 exemption (§5.4, no offside directly from a throw-in/corner/goal
  // kick) fires. Consumed here — the next kick is ordinary open play.
  evaluateOffsidePositions(state, state.players.team[striker]!, striker, state.match.restartKickPhase, state.offside);
  state.match.restartKickPhase = MatchPhase.InPlay;

  // A ball struck above head height is a HEADER, not a footed strike: less power,
  // accuracy scaled by Heading, its own anim. Possession requires the ball below
  // POSSESSION_MAX_HEIGHT (1.0m), so a header is always the loose-ball striker —
  // an owner with the ball at his feet can never trip this. (§4.3, house rule)
  const header = b.py > HEADER_CONTACT_HEIGHT;
  if (header) {
    buildHeader(state, striker, input);
  } else if (shooting) {
    buildShot(state, striker, input);
  } else {
    buildPass(state, striker, input);
  }
  applyStrike(state, striker, header ? 'header' : shooting ? 'shot' : 'pass');
  // Set a KICK ANIMATION on the strike so the renderer plays the one-shot swing
  // (§1 live defect: passes/shots produced NO kick animation — only headers and
  // bicycles set one, so a normal pass looked like the ball just left the foot).
  // A ball struck in the air below head height is a VOLLEY; otherwise Shoot for a
  // shot, Pass for a pass. Held a few ticks (kickAnimTimer) so the renderer
  // reliably samples the swing even if its frame lands between ticks; the hold is
  // an anim follow-through only — it never blocks the next kick or locomotion.
  const strikeAnim = header
    ? AnimState.Header
    : shooting
      ? (b.py > VOLLEY_HEIGHT_THRESHOLD
        ? AnimState.Volley
        : input.shootCharge < SHOT_LIGHT_CHARGE_S ? AnimState.ShootLight : AnimState.Shoot)
      : passFootAnim(state.players.yaw[striker]!, input.aimYaw);
  // UNCONDITIONAL (§11). Reaching this line means the impulse has been applied —
  // the sim ACCEPTED a strike — so it is a new execution whether or not the same
  // `AnimState` is already showing. That case is real and it is the one the
  // execution id exists for: a striker whose pass is deflected straight back and
  // played again inside KICK_ANIM_HOLD_S writes Pass over Pass, no value edge,
  // and before this the second ball left his boot with the FIRST swing still on
  // screen and no second contact event.
  beginAnimAction(state.players, striker, strikeAnim);
  // Hold the swing a few ticks (reuses actionTimer — the existing "hold this
  // pose" field). A follow-through only: locomotion still runs, and it never
  // blocks the next kick (resolveKicks is gated by input/kickCooldown, not this).
  state.players.actionTimer[striker] = Math.max(state.players.actionTimer[striker]!, KICK_ANIM_HOLD_S);
}

// ═══════════════════════════════════════════════════════════════════════════
// §2 Contact-timed actions — the ball impulse fires at the animation CONTACT
// frame, not on the input tick. Only active when state.contactTiming (Training).
// Zero-alloc scratch (never re-entered — the sim is single-threaded per tick).
// ═══════════════════════════════════════════════════════════════════════════
const _actSocket: SocketVec = { x: 0, y: 0, z: 0 };
const _actCmd: InputCommand = emptyInput(0, 0);

function pickActionKind(input: InputCommand, shooting: boolean, header = false): ActionKind {
  // A header is a header whether the button was pass or shoot — the ball is
  // above head height and the head is what reaches it.
  if (header) return ActionKind.Header;
  if (shooting) {
    if ((input.flags & InputFlags.SkillModifier) !== 0) return ActionKind.FinesseShot;
    if ((input.flags & InputFlags.Sprint) !== 0) return ActionKind.PowerShot;
    return ActionKind.NormalShot;
  }
  return (input.flags & InputFlags.ThroughBall) !== 0 ? ActionKind.ChargedPass : ActionKind.ShortPass;
}

/** Which foot contacts the ball — the side it sits on relative to the body. */
function contactFootFor(state: WorldState, id: number): ContactSocket {
  const p = state.players;
  const yaw = p.yaw[id]!;
  const rx = -dsin(yaw);
  const rz = dcos(yaw);
  const side = (state.ball.px - p.px[id]!) * rx + (state.ball.pz - p.pz[id]!) * rz;
  return side >= 0 ? ContactSocket.RightFoot : ContactSocket.LeftFoot;
}

/** Begin a contact-timed footed action: capture the release snapshot and play the
 *  wind-up. The ball has NOT moved — the impulse fires later at the contact frame. */
function startContactAction(state: WorldState, id: number, input: InputCommand, shooting: boolean): void {
  const p = state.players;
  const kind = pickActionKind(input, shooting, state.ball.py > HEADER_CONTACT_HEIGHT);
  p.actKind[id] = kind;
  p.actStartTick[id] = state.tick;
  p.actExecId[id] = (p.actExecId[id]! + 1) | 0; // bump the execution id (stale-event guard)
  p.actContacted[id] = 0;
  p.actAimYaw[id] = input.aimYaw;
  p.actPower[id] = shooting ? input.shootCharge : input.passCharge;
  p.actCurl[id] = input.curl;
  p.actChip[id] = (input.flags & InputFlags.ThroughBall) !== 0 ? 1 : 0;
  // ⚠ A HEADER CONTACTS THE HEAD. `contactFootFor` returns a boot socket, and
  // with the ball at 1.75 m the boot is ~1.6 m away — past any sane
  // contactRange, so the contact window opened and closed without ever firing
  // and the header simply never happened. (Measured the moment headers were
  // moved onto the contact path: impulse never occurred, no Header clip at
  // all.) The socket has existed all along; nothing was asking for it.
  p.actFoot[id] = kind === ActionKind.Header ? ContactSocket.Head : contactFootFor(state, id);
  // Pin the highlighted pass receiver AT RELEASE (§4 "target stays authoritative").
  p.actTarget[id] = shooting ? -1 : choosePassTarget(state, id, input.aimYaw);
  // Wind-up plays now, before any ball movement.
  const def = actionDef(kind);
  // The §2 wind-up IS the acceptance on this path, so the execution is stamped
  // here — one id covering wind-up → contact → follow-through → recovery, which
  // is what makes `actExecId` and `animExecId` name the same execution when
  // contactTiming is on.
  // The clip for the swing that is now winding up. A HEADER gets the header
  // clip whichever button asked for it — this branch was shot-or-pass only,
  // and moving headers onto the contact path therefore left them playing a
  // FOOTED swing at a ball above head height (measured: impulse correctly
  // deferred to +4 ticks, and no Header clip on screen at any point).
  beginAnimAction(p, id, kind === ActionKind.Header
    ? AnimState.Header
    : shooting
      ? (input.shootCharge < SHOT_LIGHT_CHARGE_S ? AnimState.ShootLight : AnimState.Shoot)
      : passFootAnim(p.yaw[id]!, input.aimYaw));
  p.actionTimer[id] = def.durationTicks * FIXED_DT; // hold the swing through the action
}

/** Advance every active action; fire its stored strike ONCE when the selected foot
 *  reaches the ball inside the contact window (execId/contacted guard). */
function advanceContactActions(state: WorldState, out: StepResult): void {
  const p = state.players;
  for (let id = 0; id < MAX_PLAYERS; id++) {
    const kind = p.actKind[id]! as ActionKind;
    if (kind === ActionKind.None) continue;
    if (p.active[id] !== 1) { p.actKind[id] = ActionKind.None; continue; }
    const def = actionDef(kind);
    const start = p.actStartTick[id]!;
    const phase = actionPhaseAt(start, state.tick, def);
    if (p.actContacted[id] === 0) {
      if (isTackleKind(kind)) {
        // A CHALLENGE self-gates on resolveTackle's own reach + ball-first geometry,
        // so it fires on ENTERING the contact window — no socket-proximity precondition,
        // because a mistimed challenge must still be able to FOUL (resolveTackle decides
        // clean-win / clean-miss / foul), which a socket gate would silently suppress.
        if (inContactWindow(start, state.tick, def)) {
          resolveActionStrike(state, id, kind, out);
          p.actContacted[id] = 1;
        } else if (phase >= ActionPhase.FollowThrough) {
          p.actContacted[id] = 1; // window skipped entirely — a pure whiff, nothing resolved
        }
      } else if (inContactWindow(start, state.tick, def)) {
        // FOOTED STRIKE: the selected foot socket (at this swing) must reach the ball.
        const n = normalizedActionTime(start, state.tick, def);
        computeSocket(state, id, p.actFoot[id]! as ContactSocket, _actSocket, n);
        const b = state.ball;
        const dx = b.px - _actSocket.x;
        const dz = b.pz - _actSocket.z;
        const dy = b.py - _actSocket.y;
        const dist = Math.sqrt(dx * dx + dz * dz + dy * dy);
        // Hit within range, or inside the small capped correction band.
        //
        // ── THE SECOND TEST, and why the first one alone was a defect ────────
        // `findStriker` ACCEPTED this input because the ball was inside
        // `inFootReach` (BALL_PLAY_REACH, 0.7 m of the striker, at a strikeable
        // height). `contactTiming` then defers the impulse ~12 ticks, and this
        // line judged it again against a STRICTER rule — 0.55 m of the boot
        // SOCKET, which sits ~0.26 m ahead of the player and 0.11 m to one
        // side. A ball still perfectly at his feet by the game's own standard
        // therefore failed here, silently: no impulse, no event, the input
        // spent, and on screen a full swing through empty air.
        //
        // MEASURED over 12 seeds x 240 s of 11v11 on the practice path: 14.7 %
        // of every pass press (74 of 502) ended this way. Of those whiffs the
        // boot's closest approach was p50 0.75 m / p90 1.41 m — but the ball
        // was p50 0.49 m / p90 0.67 m from the STRIKER, i.e. inside the 0.7 m
        // reach that admitted the strike in the first place, in over half of
        // them. The same 12 seeds on the immediate path whiff 0.0 % of 519.
        //
        // So: a footed strike also connects while the ball is still inside the
        // reach that authorised it. That is not a magnet — it is the same
        // envelope, applied consistently at both ends of the deferral. A ball
        // that has genuinely left (tackled away, knocked past him) is outside
        // it and still whiffs, which is the honest miss this branch is for.
        if (
          dist <= def.contactRange + def.maxCorrection ||
          inFootReach(p.px[id]!, p.pz[id]!, p.py[id]!, b.px, b.py, b.pz)
        ) {
          resolveActionStrike(state, id, kind, out);
          p.actContacted[id] = 1;
        }
      } else if (phase >= ActionPhase.FollowThrough) {
        // Contact window closed without reaching the ball → MISSED: no remote kick,
        // the ball stays physical, the charged input is spent.
        p.actContacted[id] = 1;
      }
    }
    if (phase === ActionPhase.Complete) p.actKind[id] = ActionKind.None; // action done
  }
}

/** Fire the stored strike at contact, reproducing the release snapshot. */
function resolveActionStrike(state: WorldState, id: number, kind: ActionKind, out: StepResult): void {
  if (isTackleKind(kind)) {
    // A CHALLENGE (standing/poke/slide): transfer/dislodge the ball — or foul — at
    // THIS contact frame instead of the input tick, via the tackle resolver.
    resolveContactTackle(state, id, kind, out);
    return;
  }
  const p = state.players;
  const cmd = _actCmd;
  cmd.flags = 0; cmd.moveX = 0; cmd.moveZ = 0; cmd.liftBias = 0; cmd.skill = SkillMove.None;
  cmd.shootCharge = 0; cmd.passCharge = 0;
  cmd.aimYaw = p.actAimYaw[id]!;
  cmd.curl = p.actCurl[id]!;
  // A header rebuilds as a header: buildHeader owns its own power and the
  // Heading-scaled accuracy, so neither the shot nor the pass branch applies.
  if (kind === ActionKind.Header) {
    cmd.flags = InputFlags.Shoot;
    cmd.shootCharge = p.actPower[id]!;
    buildHeader(state, id, cmd);
    applyStrike(state, id, 'header');
    evaluateOffsidePositions(state, p.team[id]!, id, state.match.restartKickPhase, state.offside);
    state.match.restartKickPhase = MatchPhase.InPlay;
    return;
  }
  const shooting = kind === ActionKind.NormalShot || kind === ActionKind.PowerShot || kind === ActionKind.FinesseShot;
  const target = p.actTarget[id]!;
  if (!shooting && target >= 0 && p.active[target] === 1) {
    // Aim straight at the pinned receiver's CURRENT spot — the release target wins.
    cmd.aimYaw = datan2(p.pz[target]! - p.pz[id]!, p.px[target]! - p.px[id]!);
  }
  if (shooting) {
    cmd.shootCharge = p.actPower[id]!;
    cmd.flags = InputFlags.Shoot
      | (kind === ActionKind.FinesseShot ? InputFlags.SkillModifier : 0)
      | (kind === ActionKind.PowerShot ? InputFlags.Sprint : 0)
      // The chip survives the deferral (see PlayerArrays.actChip).
      | (p.actChip[id] === 1 ? InputFlags.ThroughBall : 0);
    buildShot(state, id, cmd);
    applyStrike(state, id, 'shot');
  } else {
    cmd.passCharge = p.actPower[id]!;
    cmd.flags = InputFlags.Pass | (kind === ActionKind.ChargedPass ? InputFlags.ThroughBall : 0);
    buildPass(state, id, cmd);
    applyStrike(state, id, 'pass');
  }
  // Offside snapshot at the ACTUAL strike (Law-11 timing). No-op when offside is off.
  evaluateOffsidePositions(state, p.team[id]!, id, state.match.restartKickPhase, state.offside);
  state.match.restartKickPhase = MatchPhase.InPlay;
}

/**
 * The single outfield player who may bicycle-kick this tick, or −1. Legal only
 * when: the BicycleKick flag is set, the ball is airborne (≥ BICYCLE_MIN_BALL_HEIGHT)
 * AND within BICYCLE_REACH horizontally AND overhead-to-behind relative to facing
 * (dot(facing, dirToBall) ≤ BICYCLE_MAX_FORWARD_DOT, or directly overhead), and the
 * player has a real shot charge. Keepers are excluded (their aerial tool is the
 * dive). Nearest legal requester to the ball wins. (house rule 2026-07-17)
 */
function findBicycleStriker(state: WorldState, inputs: ReadonlyArray<InputCommand | null>): number {
  const p = state.players;
  const b = state.ball;
  if (b.py < BICYCLE_MIN_BALL_HEIGHT) return -1; // ball too low for any bicycle

  let best = -1;
  let bestDist = BICYCLE_REACH; // must be within horizontal reach to count
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.gkHoldStartTick[i]! >= 0) continue;
    if (state.positions[i] === 'GK') continue; // outfield only, like the jump
    const inp = inputs[i];
    if (inp == null || (inp.flags & InputFlags.BicycleKick) === 0) continue;
    if (inp.shootCharge < SHOT_CHARGE_MIN_S) continue; // needs the normal charge

    const dx = b.px - p.px[i]!;
    const dz = b.pz - p.pz[i]!;
    const hlen = Math.sqrt(dx * dx + dz * dz);
    if (hlen > BICYCLE_REACH) continue;
    // Overhead-to-behind geometry: a ball out in front is a plain volley, not a
    // bicycle. A ball basically overhead (tiny horizontal offset) always qualifies.
    if (hlen >= BICYCLE_OVERHEAD_RADIUS) {
      const dot = (dcos(p.yaw[i]!) * dx + dsin(p.yaw[i]!) * dz) / hlen;
      if (dot > BICYCLE_MAX_FORWARD_DOT) continue;
    }
    if (hlen < bestDist) {
      bestDist = hlen;
      best = i;
    }
  }
  return best;
}

/**
 * §4.2 — CAN THIS PLAYER'S BODY ACTUALLY GET TO THE BALL? dot(facing, ball) must
 * be at least STRIKE_MIN_FACING_DOT, i.e. the ball is not behind his shoulders.
 *
 * The owner reported "sometimes players are showing back to the ball and kicking",
 * and MEASURED over three seeded 11v11 soaks (ai/ball-engagement.test.ts) that was
 * 20.1 % of every strike in the match — p10 of the facing dot was −0.768 and the
 * worst was −0.996, a ball struck almost exactly behind the heel.
 *
 * It is not an animation artifact. player-controller.ts:1404 turns the body toward
 * the aim through `rotateTowards`, i.e. at a RATE LIMIT, and resolveKicks runs at
 * stage 6 of step.ts — after that turn — applying the impulse IMMEDIATELY from
 * `input.aimYaw`. The kick is therefore free to beat the body around by design,
 * and with nothing testing the body it did so without limit.
 *
 * A player standing ON the ball (hlen below the degenerate threshold) always
 * passes: there is no meaningful direction to the ball, and refusing the strike
 * there would be a divide-by-zero dressed up as a rule. Guarded, not assumed —
 * a NaN here propagates into the world hash.
 */
function facingAllowsStrike(state: WorldState, id: number): boolean {
  const p = state.players;
  const b = state.ball;
  const dx = b.px - p.px[id]!;
  const dz = b.pz - p.pz[id]!;
  const hlen = Math.sqrt(dx * dx + dz * dz);
  if (hlen < STRIKE_FACING_MIN_DIST) return true; // on top of it — no direction to test
  const dot = (dcos(p.yaw[id]!) * dx + dsin(p.yaw[id]!) * dz) / hlen;
  if (dot >= STRIKE_MIN_FACING_DOT) return true;

  // ── THE FIRST-TIME EXEMPTION, and why it has to exist ──────────────────────
  //
  // In this sim `yaw` IS the aim: player-controller.ts:1393 sets targetYaw =
  // aimYaw. So a player redirecting an incoming ball through a wide angle CANNOT
  // also be facing it — his body is already opening to where the ball is going,
  // which is exactly what a real player's body does on a first-time pass. A pure
  // facing gate therefore outlaws every first-time redirect past 90 deg, which is
  // real football and is asserted by sim/pass-quality.test.ts's `first-time` row.
  //
  // The discriminator that separates that from the defect is whether the ball is
  // COMING TO HIM. A ball closing on the player is one he is receiving, and
  // flicking it on is a skill. A ball behind him and NOT closing is one he has
  // already left behind, and striking that is the thing the owner saw. So a
  // closing ball may be struck from further round — down to
  // STRIKE_ARRIVING_MIN_FACING_DOT, still short of a strike behind the heel.
  const rvx = b.vx - p.vx[id]!;
  const rvz = b.vz - p.vz[id]!;
  // Closing speed along the player→ball axis, positive = the gap is shrinking.
  const closing = -(rvx * dx + rvz * dz) / hlen;
  if (closing >= STRIKE_ARRIVING_CLOSING_SPEED && dot >= STRIKE_ARRIVING_MIN_FACING_DOT) return true;
  return false;
}

/**
 * Did player `id` ask to strike the ball this tick? A Pass/Shoot bit on the
 * input. Every gate that reads "he wants to kick it" goes through here so
 * they can never disagree.
 */
function strikeRequested(_state: WorldState, _id: number, inp: InputCommand | null | undefined): boolean {
  return inp != null && (inp.flags & (InputFlags.Pass | InputFlags.Shoot)) !== 0;
}

/** The single player who strikes the ball this tick, or −1. */
function findStriker(state: WorldState, inputs: ReadonlyArray<InputCommand | null>): number {
  const p = state.players;
  const b = state.ball;

  // A STAGED RESTART is exempt: rules/fsm.ts owns that geometry (it walks the
  // taker onto a mark and gates who may play the ball) and it has its own
  // auto-take timeout. Gating the taker's facing here could let a restart expire
  // unplayed, which is a deadlock, not a realism win.
  const restartTake = state.match.phase !== MatchPhase.InPlay;

  // Possession PRIORITISES the owner, but does not license a strike on its own:
  // possession is bookkeeping out to POSSESSION_RADIUS (1.2m), and you cannot kick
  // a ball your boot cannot reach. The owner strikes only when the ball is inside
  // foot reach; otherwise he must let it come to his feet (fall through to the
  // loose scan, which also requires reach — so a possessor at 1.0m simply doesn't
  // strike this tick). Measured pre-fix: passes fired at ~0.7m, shots at ~1.2m off
  // the foot — the "ball moves before he reaches it" defect. (§4.2, contact truth)
  const owner = b.possessedBy;
  if (owner >= 0 && p.gkHoldStartTick[owner]! < 0) {
    const inp = inputs[owner];
    // THE CARRIER'S OWN BALL IS ALWAYS PLAYABLE. inFootReach (0.7 m) is the
    // generic gate; a sprint dribble rides 0.83-0.87 m ahead BY DESIGN, so a
    // sprinting carrier's pass tap waited forever (owner: "pass while running
    // is not happeing" - reproduced: 40+ ticks, never accepted). The owner's
    // next touch is the strike: reach STRIKE_OWN_BALL_REACH for the ball he
    // himself is dribbling, at dribble height. Loose balls keep 0.7.
    const odx = b.px - p.px[owner]!;
    const odz = b.pz - p.pz[owner]!;
    // ⚠ A MOVING BALL ONLY. Dribble knocks do not stamp lastTouchedBy, so the
    // honest dribble signal is the ball MOVING while possessed - a static
    // ball a metre away is an approach, not a dribble, and keeps the 0.7 m
    // gate (contact-truth's premature guard). 0.5 m/s separates them cleanly:
    // a sprint dribble rides at 6-8 m/s, a walking dribble above 1.
    const ballSpeedSq = b.vx * b.vx + b.vz * b.vz;
    const ownReach = ballSpeedSq > 0.5 * 0.5
      && odx * odx + odz * odz <= STRIKE_OWN_BALL_REACH * STRIKE_OWN_BALL_REACH
      && b.py <= POSSESSION_MAX_HEIGHT;
    if (
      strikeRequested(state, owner, inp) &&
      (ownReach || inFootReach(p.px[owner]!, p.pz[owner]!, p.py[owner]!, b.px, b.py, b.pz)) &&
      (restartTake || facingAllowsStrike(state, owner))
    ) {
      return owner;
    }
  }

  // Otherwise a loose/airborne ball can be struck by the nearest player in reach
  // who asked to — this is what lets volleys, headers and first-time clearances
  // happen. The vertical envelope is now FEET-RELATIVE: a player reaches the ball
  // up to their own feet height plus the foot-volley ceiling plus a header/leap
  // margin (STRIKE_MAX_HEIGHT + HEADER_EXTRA_REACH). A grounded player tops out at
  // ~1.65m (a standing header); an airborne player adds their py, so a well-timed
  // jump reaches a ~2m ball a grounded player physically cannot. (§4.3, house rule)
  let best = -1;
  let bestDist = Number.POSITIVE_INFINITY;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.gkHoldStartTick[i]! >= 0) continue;
    const inp = inputs[i];
    if (!strikeRequested(state, i, inp)) continue;
    if (b.py > p.py[i]! + STRIKE_MAX_HEIGHT + HEADER_EXTRA_REACH) continue; // above this player's reach
    const d = distXZ(p.px[i]!, p.pz[i]!, b.px, b.pz);
    // Horizontal reach is BALL_PLAY_REACH, not POSSESSION_RADIUS: you may OWN a
    // ball at 1.2m, but you cannot strike one you are not close enough to touch.
    // Measured live on the old gate: 8 m/s shots leaving a foot 1.2m from the
    // ball — the ball simply left, untouched, which is what "the ball moves
    // before it touched players" looks like from the stands.
    if (d > BALL_PLAY_REACH || d >= bestDist) continue;
    // You cannot volley, clear or head a ball that is behind your shoulders. See
    // facingAllowsStrike — this is the gate the measured 20.1 % of back-to-ball
    // strikes was missing. (The bicycle kick is unaffected: findBicycleStriker
    // runs FIRST and deliberately requires the opposite geometry.)
    if (!restartTake && !facingAllowsStrike(state, i)) continue;
    bestDist = d;
    best = i;
  }
  return best;
}

/**
 * Manual aim is the default (§4.2): the direction comes straight from the aim
 * yaw. A through-ball is the exception — it leads a runner. We find the best
 * teammate inside the aim cone and aim at where he WILL be, from his velocity and
 * the time the ball needs to reach him. The lead is capped so a sprinting runner
 * doesn't drag the pass off the pitch.
 *
 * Writes the horizontal aim direction into (aimOut.x, aimOut.z).
 */
/**
 * §4 — the point a to-feet pass should fly to: the receiver's CONTROL FOOT.
 *
 * A single dist/speed lead under-shoots a moving man (he keeps running through the
 * ball's flight, so the true intercept is further along — measured ~2 m short of a
 * 6 m/s runner). Iterate the intercept to convergence, then bias ONE STEP ahead
 * into his stride so the ball meets his control foot and he takes it in motion.
 * A stationary/slow man gets no step (the pass is simply to his feet). Writes the
 * lead point into (leadOut.x, leadOut.z).
 */
const leadOut = { x: 0, z: 0 };
const leadTravel = createBallTravel();
function passControlFootPoint(
  p: WorldState['players'],
  sx: number,
  sz: number,
  targetId: number,
  passSpeed: number,
  airborne: boolean,
): void {
  const tvx = p.vx[targetId]!;
  const tvz = p.vz[targetId]!;
  const tvsp0 = Math.sqrt(tvx * tvx + tvz * tvz);
  // ── LEAD CEILING, and why the time cap alone is not one ───────────────────
  // The fixed point below is only stable while the ball's EFFECTIVE speed over
  // the flight stays comfortably above the runner's. Once the flight time came
  // from the real (decelerating) ball, that stopped being true at range: a
  // longer lead point means a longer flight, which means a longer lead, and for
  // a fast lateral runner the iteration walks out to THROUGH_BALL_LEAD_CAP_S.
  // MEASURED over 12 seeds after the honest flight time went in and before this
  // cap: the launch bearing's deviation from the requested aim went p90 33.6 ->
  // 43.0 deg and p99 61.6 -> 147.9 deg — i.e. a pass occasionally left almost
  // backwards from where it was pointed, because the lead point had swung past
  // the passer. Long-ball completion fell with it (48.5 -> 40.7 %).
  //
  // So the lead is also capped in SPACE, as a fraction of how far the man
  // actually is. You lead a man 3 m away by a stride, not by a second of his
  // run; you lead a man 25 m away by several metres. That is one number, it
  // bounds the bearing swing geometrically (asin of the fraction), and it makes
  // the iteration convergent again.
  const leadCapM = PASS_LEAD_MAX_FRAC * distXZ(sx, sz, p.px[targetId]!, p.pz[targetId]!);
  const leadTCap = tvsp0 > 1e-6
    ? Math.min(THROUGH_BALL_LEAD_CAP_S, leadCapM / tvsp0)
    : THROUGH_BALL_LEAD_CAP_S;
  let leadT = 0;
  for (let it = 0; it < PASS_LEAD_ITERS; it++) {
    const fx = p.px[targetId]! + tvx * leadT;
    const fz = p.pz[targetId]! + tvz * leadT;
    const s = distXZ(sx, sz, fx, fz);
    // ── FLIGHT TIME, honestly (§4.2) ──────────────────────────────────────
    // This used to be `s / passSpeed`, i.e. the ball never slows down. It does:
    // a grounded ball in this sim is permanently SKIDDING and sheds 7-12 m/s²
    // (sim/ground-travel.ts, measured tick-by-tick off this pipeline), so the
    // real flight takes 30-45 % longer than `s / v0` and the runner is metres
    // further on than the lead assumed. MEASURED before this change, receiver
    // running at 4 m/s, crosshair pointed AT him, signed miss along his own
    // direction of travel at closest approach (negative = behind him):
    //     10 m across −0.51 m   18 m across −0.88 m   26 m across −1.56 m
    //     10 m away   −0.95 m   18 m away   −0.70 m   26 m away   −1.53 m
    // Every sign negative, growing with distance: the signature of a lead that
    // is systematically too short. At 26 m the receiver never latched (0/3).
    //
    // A LOFTED ball is exempt and must stay exempt: it spends its flight in the
    // air, where drag is small next to the turf's Coulomb bite, so the ground
    // law would over-lead a cross by as much as it used to under-lead a roll.
    // Its horizontal speed is close to constant, which is what `s / v` says.
    if (airborne) {
      leadT = clamp(s / passSpeed, 0, leadTCap);
    } else {
      travelForDistance(passSpeed, s, leadTravel);
      // ── DO NOT LEAD TO A POINT THE BALL CANNOT GET TO ────────────────────
      // `reached` is false when the ball stops short of the lead point, or is
      // still short of it when the travel horizon expires. Aiming there anyway
      // is how a long ball to a runner becomes a giveaway: the power fit below
      // is refitted to that unreachable distance, pins on GROUND_PASS_MAX_SPEED
      // and the ball runs straight past everybody. MEASURED over 12 seeds with
      // the honest flight time but no reachability test, passes that travelled
      // over 20 m before the next touch went from 48.5 % completed / 28.7 m
      // median travel to 39.0 % / 37.9 m, with 19.8 % of them never touched by
      // anyone. On the ground you cannot slide a 30 m ball into a runner's
      // path — you drive it (Sprint + Pass) or you play a different pass — so
      // when the intercept is not reachable the pass goes to the man where he
      // IS, plus the one stride below.
      leadT = leadTravel.reached ? clamp(leadTravel.time, 0, leadTCap) : 0;
    }
  }
  const tvsp = Math.sqrt(tvx * tvx + tvz * tvz);
  const step = tvsp > 0.5 ? PASS_LEAD_CONTROL_FOOT_STEP : 0;
  const sdx = tvsp > 0.5 ? tvx / tvsp : 0;
  const sdz = tvsp > 0.5 ? tvz / tvsp : 0;
  leadOut.x = p.px[targetId]! + tvx * leadT + sdx * step;
  leadOut.z = p.pz[targetId]! + tvz * leadT + sdz * step;
}

const aimOut = { x: 0, z: 0 };
function resolveAim(
  state: WorldState,
  striker: number,
  aimYaw: number,
  passSpeed: number,
  lead: boolean,
  presetTarget = -1,
): void {
  const dirx = dcos(aimYaw);
  const dirz = dsin(aimYaw);
  // A pre-scored assist target (buildPass → choosePassTarget) is aimed at with
  // the same velocity lead a through-ball gets — pass to the MAN. Without one,
  // a plain pass flies the raw bearing and a through-ball scans its own cone.
  let bestId = presetTarget;
  if (bestId < 0 && (!lead || passSpeed <= 0)) {
    aimOut.x = dirx;
    aimOut.z = dirz;
    return;
  }

  const p = state.players;
  const team = p.team[striker]!;
  const sx = p.px[striker]!;
  const sz = p.pz[striker]!;

  if (bestId < 0) {
    const coneCos = dcos(ASSIST_CONE_DEG * DEG2RAD);
    let bestAlign = coneCos;
    for (let i = 0; i < MAX_PLAYERS; i++) {
      if (i === striker || p.active[i] !== 1 || p.sentOff[i] === 1) continue;
      if (p.team[i]! !== team) continue;
      const tx = p.px[i]! - sx;
      const tz = p.pz[i]! - sz;
      const len = Math.sqrt(tx * tx + tz * tz);
      if (len < 1e-6) continue;
      const align = (tx * dirx + tz * dirz) / len;
      if (align > bestAlign) {
        bestAlign = align;
        bestId = i;
      }
    }
  }

  if (bestId < 0) {
    aimOut.x = dirx;
    aimOut.z = dirz;
    return;
  }

  // Lead the runner onto his control foot (§4): a converged intercept plus one
  // step into his stride, so the ball reaches him instead of the space he left.
  // `lead` is set only for the LOFTED gesture, which is also the one whose
  // flight time the ground-skid law must not be applied to.
  passControlFootPoint(p, sx, sz, bestId, passSpeed, lead);
  const aimx = leadOut.x - sx;
  const aimz = leadOut.z - sz;
  const len = Math.sqrt(aimx * aimx + aimz * aimz);
  if (len < 1e-6) {
    aimOut.x = dirx;
    aimOut.z = dirz;
  } else {
    aimOut.x = aimx / len;
    aimOut.z = aimz / len;
  }
}

/**
 * Pressure from the nearest opponent, 0 (free) .. 1 (right on top of you). Feeds
 * the pass/shot error models (§4.2, §4.3). Allocation-free scan.
 */
function pressureOn(state: WorldState, id: number): number {
  const p = state.players;
  const team = p.team[id]!;
  const px = p.px[id]!;
  const pz = p.pz[id]!;
  let nearest = Number.POSITIVE_INFINITY;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1 || p.team[i]! === team) continue;
    const d = distXZ(px, pz, p.px[i]!, p.pz[i]!);
    if (d < nearest) nearest = d;
  }
  if (nearest >= PRESSURE_RADIUS) return 0;
  return 1 - nearest / PRESSURE_RADIUS;
}

/** Horizontal player speed — the "at pace" term in both error models. */
function playerSpeedXZ(state: WorldState, id: number): number {
  const p = state.players;
  return Math.sqrt(p.vx[id]! * p.vx[id]! + p.vz[id]! * p.vz[id]!);
}

// ---------------------------------------------------------------------------
// §4 KICK DIRECTIVE gains (owner, 2026-07-25) — the two gates the directive put
// on aimed kicks. Full reasoning: the KICK DIRECTIVE block at the end of
// constants.ts. Read PER WORLD from MatchConfig, which defaults to the shipped
// constants (both OFF), so:
//   - production behaviour is decided in exactly one place, and
//   - the curve/strike-error suites can build a world with the gate ON and keep
//     proving the mechanic works instead of asserting the dead path and rotting.
//
// They return a 0/1 MULTIPLIER, never a branch. The RNG draws these scale MUST
// still happen in the same order and count whatever the gate says (§13.1, D10):
// branch around a draw and a world with the gate flipped consumes a different
// number of draws than production, which desyncs the shared stream. Multiply.
// ---------------------------------------------------------------------------

/** 1 = curve as designed, 0 = deliberate lateral spin suppressed. */
function curveGain(state: WorldState): number {
  return state.config.curveEnabled ? 1 : 0;
}

/** 1 = aimed-strike error cone as designed, 0 = the aimed kick is true. */
function aimErrorGain(state: WorldState): number {
  return state.config.strikeAimErrorEnabled ? 1 : 0;
}

// ---------------------------------------------------------------------------
// Passing (§4.2)
// ---------------------------------------------------------------------------

/**
 * Resolve a pass into the `kick` scratch. Ground / driven / lofted / through /
 * cross are all one gesture (hold Pass) shaded by two modifiers:
 *   Sprint    → driven (low, fast).
 *   ThroughBall → lofted, and if taken from wide + advanced, a cross.
 * Otherwise it is a ground pass. A through-ball also leads the runner.
 */
function buildPass(state: WorldState, striker: number, input: InputCommand): void {
  const attr = state.attributes[striker]!;

  const t = clamp01(
    (clamp(input.passCharge, PASS_CHARGE_MIN_S, PASS_CHARGE_MAX_S) - PASS_CHARGE_MIN_S) /
    (PASS_CHARGE_MAX_S - PASS_CHARGE_MIN_S),
  );

  const lofted = (input.flags & InputFlags.ThroughBall) !== 0;
  const driven = !lofted && (input.flags & InputFlags.Sprint) !== 0;

  let speed: number;
  let angleDeg: number;
  let backspin: number;
  if (driven) {
    speed = lerpN(DRIVEN_PASS_MIN_SPEED, DRIVEN_PASS_MAX_SPEED, t);
    angleDeg = 0;
    backspin = DRIVEN_PASS_BACKSPIN;
  } else if (lofted) {
    speed = lerpN(GROUND_PASS_MIN_SPEED, GROUND_PASS_MAX_SPEED, t);
    // A lofted ball from wide + advanced areas is a cross into the box. (§4.2)
    if (isCrossPosition(state, striker)) {
      angleDeg = lerpN(CROSS_MIN_ANGLE_DEG, CROSS_MAX_ANGLE_DEG, t);
    } else {
      angleDeg = lerpN(LOFTED_PASS_MIN_ANGLE_DEG, LOFTED_PASS_MAX_ANGLE_DEG, t);
    }
    backspin = GROUND_PASS_BACKSPIN;
  } else {
    speed = lerpN(GROUND_PASS_MIN_SPEED, GROUND_PASS_MAX_SPEED, t);
    angleDeg = 0;
    backspin = GROUND_PASS_BACKSPIN; // slight backspin (§4.2)
  }

  // ── Pass-target assist (§4.2, Phase 3B) ──────────────────────────────────
  // A ground/lofted pass is FOR somebody: resolve the aim to the most sensible
  // teammate near it (pass-target.ts — the same scorer the 3C indicator draws)
  // and scale power to the man: soft to feet at 6 m, firm over 20 m. A TAP is
  // that fitted weight — the one measured to arrive collectable — and the charge
  // bar then drives it harder, spending PASS_ASSIST_CHARGE_HEADROOM of the pace
  // left up to the ground-pass ceiling. A DRIVEN ball is exempt on power (it is
  // a cannon by request), and when no teammate is near the aim the raw bearing +
  // raw charge fly unchanged — the human always keeps the override.
  const assistTarget = driven ? -1 : choosePassTarget(state, striker, input.aimYaw);
  if (assistTarget >= 0 && !driven) {
    const p = state.players;
    const sx = p.px[striker]!;
    const sz = p.pz[striker]!;
    // Fit the power to the SAME control-foot lead point the aim flies at (§4), not
    // the man's current spot — a receding runner adds metres during flight, so a
    // power fitted on his current distance lands short. A first-pass speed
    // estimate seeds the converged intercept, then power is refit to it.
    const d0 = distXZ(sx, sz, p.px[assistTarget]!, p.pz[assistTarget]!);
    const s0 = PASS_ASSIST_SPEED_BASE + PASS_ASSIST_SPEED_PER_M * d0;
    passControlFootPoint(p, sx, sz, assistTarget, s0, lofted);
    const targetDist = distXZ(sx, sz, leadOut.x, leadOut.z);
    const fit = PASS_ASSIST_SPEED_BASE + PASS_ASSIST_SPEED_PER_M * targetDist;
    const fitted = clamp(fit, GROUND_PASS_MIN_SPEED, GROUND_PASS_MAX_SPEED);
    // The charge spends a fraction of the pace LEFT between the weighted pass
    // and the hardest ground pass there is. Cannot exceed the ceiling, so it
    // cannot bunch a band of passes onto it — see PASS_ASSIST_CHARGE_HEADROOM.
    speed = fitted + (GROUND_PASS_MAX_SPEED - fitted) * PASS_ASSIST_CHARGE_HEADROOM * t;
  }

  resolveAim(state, striker, input.aimYaw, speed, lofted, assistTarget);

  // Error (§4.2): (1 - Passing)·pressure·(speed/max)·gaussian, expressed through
  // statMultiplier so a single stat stays inside the ±15% cap §8.1 calls
  // essential — the same choice player-controller.ts makes for interceptions.
  // 2-D like the shot cone: an independent vertical gaussian mis-weights the
  // loft (a floated cross dies short, a scuffed ground ball bobbles) — clamped
  // at 0 so half of all ground-pass misses stay honestly flat.
  const speedFrac = clamp01(playerSpeedXZ(state, striker) / MAX_SPEED_SPRINT);
  const pressureFactor = 1 + pressureOn(state, striker) * PRESSURE_ERROR_WEIGHT;
  const passErrScale = pressureFactor * (0.25 + speedFrac);
  // ⚠ aimErrorGain is 0 while the owner's "kicks 100% accurate" directive is in
  // force (constants.ts KICK DIRECTIVE block). It MULTIPLIES the draw —
  // pcg32GaussianClamped still runs, twice, in this order, so the shared RNG
  // stream is unchanged and old replays still reproduce. Never fold the gain into
  // an `if` that skips the draw.
  const errGain = aimErrorGain(state);
  const errorRad =
    (PASS_BASE_ERROR_RAD / statMultiplier(attr.passing)) *
    passErrScale * errGain * pcg32GaussianClamped(state.rng);
  const vertErrDeg =
    (PASS_VERTICAL_ERROR_DEG / statMultiplier(attr.passing)) *
    passErrScale * errGain * pcg32GaussianClamped(state.rng);
  const finalAngleDeg = clamp(angleDeg + vertErrDeg, 0, CROSS_MAX_ANGLE_DEG + 8);

  const yaw = wrapAngle(datan2(aimOut.z, aimOut.x) + errorRad);
  const cosA = dcos(finalAngleDeg * DEG2RAD);
  const sinA = dsin(finalAngleDeg * DEG2RAD);
  // Same boost-2 power bonus buildShot applies, same gate (stamina > 0). Not
  // folded into `speed` earlier — the assist-fit and error-scale math above
  // are tuned against the UN-boosted pace, same reason boostAura's tier VFX
  // are additive rather than baked into the base numbers.
  const boostedTier2 = (input.flags & InputFlags.Boost2) !== 0 && state.players.stamina[striker]! > 0;
  const finalSpeed = boostedTier2 ? speed * BOOST2_KICK_POWER_MULTIPLIER : speed;
  kick.vx = dcos(yaw) * finalSpeed * cosA;
  kick.vz = dsin(yaw) * finalSpeed * cosA;
  kick.vy = finalSpeed * sinA;
  // ── THE APEX FLOOR: A LOFT MUST CLEAR A MAN (owner, 2026-08-15) ──────────
  // The arc above is solved from the RECEIVER'S DISTANCE alone, so a short
  // lofted pass came out at 1.26-1.81 m of apex - measured in the running
  // game, under a standing player's head. Nobody presses the loft modifier to
  // pass UNDER someone. The floor rises with the charge ("it should go from
  // above the player as per the kick force"): minimum charge clears any
  // player, full charge sails. It only ever LIFTS - a long ball whose own
  // solve is already higher keeps it.
  if (lofted) {
    const wantVy = Math.sqrt(2 * -GRAVITY * lerpN(LOFT_APEX_MIN_M, LOFT_APEX_MAX_M, t));
    if (kick.vy < wantVy) kick.vy = wantVy;
  }
  // Same "boost-2 OR full charge" wave trigger buildShot uses — see its
  // comment for why this is a different condition from the speed bonus above.
  kick.powerKick = boostedTier2 || t >= 1;
  kick.backspin = backspin;
  // Mouse-gesture curl bends passes too (§4.2 house rule, polish phase): the
  // same clamped client gesture that curls a shot, scaled by Passing (±15%) and
  // by pass power (a firmer strike carries more of the gesture's spin — and the
  // Magnus force needs speed to act on, so a soft curled pass bends gently by
  // physics anyway). Driven passes take a reduced factor: a laces-through drive
  // is the wrong technique for bend. Accuracy cost under pressure/at pace is
  // already in errorRad above. Capped by the clamp + clampSpin at the strike.
  // ⚠ curveGain is 0 while the owner's "remove the curve kick for now" directive
  // is in force (constants.ts KICK DIRECTIVE block) — the whole model below is
  // intact and one boolean brings it back.
  const curveTechnique = driven ? DRIVEN_PASS_CURL_FACTOR : 1;
  kick.sidespin =
    curveGain(state) *
    MOUSE_CURL_SIDESPIN *
    clamp(input.curl, -1, 1) *
    statMultiplier(attr.passing) *
    (PASS_CURL_POWER_FLOOR + (1 - PASS_CURL_POWER_FLOOR) * t) *
    curveTechnique;
}

/**
 * WHAT WOULD LEAVE THE BOOT IF HE STRUCK IT NOW — without striking it.
 *
 * The launch half of the corner landing trail (client/src/render/cornerTrail.ts).
 * `ball-predict.ts` integrates the result forward through the real drag+Magnus
 * model; this is where the numbers it integrates come from.
 *
 * ── Why it lives HERE, next to buildPass, and not in the client ─────────────
 *
 * Because it has to be the same arithmetic, and a copy in the client would be a
 * second implementation free to drift — which is precisely how the deleted 7 m
 * marker came to mean nothing. This shares `isCrossPosition`, `choosePassTarget`,
 * `passControlFootPoint` and `resolveAim` with the real strike by calling them,
 * so the assist override that REPLACES the charge-derived speed (the single
 * biggest term in a corner's range) is the real one, not an estimate.
 *
 * ── The two deliberate differences from buildPass, and why they are safe ────
 *
 * 1. NO RNG DRAWS. buildPass calls `pcg32GaussianClamped(state.rng)` twice, and
 *    this must never do that: it runs on the render path, and advancing the
 *    shared stream from there would desync every client from the server and
 *    break replay reproduction (D1). It is sound to omit because both draws are
 *    multiplied by `aimErrorGain(state)`, which is 0 while STRIKE_AIM_ERROR_
 *    ENABLED is false — the KICK DIRECTIVE, constants.ts. `strikeAimErrorEnabled`
 *    is therefore reported back through `exact`: when the sim is rolling real
 *    aim error the preview is the CENTRE of the cone, not the shot, and the
 *    caller is told so rather than left to assume.
 * 2. NOTHING IS MUTATED. Not the ball, not the player, not `kick`.
 *
 * Returns false when there is nothing honest to draw.
 */
export function previewPassLaunch(
  state: WorldState,
  striker: number,
  aimYaw: number,
  passCharge: number,
  lofted: boolean,
  out: PreviewLaunch,
): boolean {
  const p = state.players;
  if (striker < 0 || striker >= MAX_PLAYERS) return false;
  if (p.active[striker] !== 1 || p.sentOff[striker] === 1) return false;

  const t = clamp01(
    (clamp(passCharge, PASS_CHARGE_MIN_S, PASS_CHARGE_MAX_S) - PASS_CHARGE_MIN_S) /
    (PASS_CHARGE_MAX_S - PASS_CHARGE_MIN_S),
  );

  let speed: number;
  let angleDeg: number;
  if (lofted) {
    speed = lerpN(GROUND_PASS_MIN_SPEED, GROUND_PASS_MAX_SPEED, t);
    angleDeg = isCrossPosition(state, striker)
      ? lerpN(CROSS_MIN_ANGLE_DEG, CROSS_MAX_ANGLE_DEG, t)
      : lerpN(LOFTED_PASS_MIN_ANGLE_DEG, LOFTED_PASS_MAX_ANGLE_DEG, t);
  } else {
    speed = lerpN(GROUND_PASS_MIN_SPEED, GROUND_PASS_MAX_SPEED, t);
    angleDeg = 0;
  }
  const backspin = GROUND_PASS_BACKSPIN;

  // The same assist override the real strike takes — see buildPass.
  const assistTarget = choosePassTarget(state, striker, aimYaw);
  if (assistTarget >= 0) {
    const sx = p.px[striker]!;
    const sz = p.pz[striker]!;
    const d0 = distXZ(sx, sz, p.px[assistTarget]!, p.pz[assistTarget]!);
    const s0 = PASS_ASSIST_SPEED_BASE + PASS_ASSIST_SPEED_PER_M * d0;
    passControlFootPoint(p, sx, sz, assistTarget, s0, lofted);
    const targetDist = distXZ(sx, sz, leadOut.x, leadOut.z);
    const fit = PASS_ASSIST_SPEED_BASE + PASS_ASSIST_SPEED_PER_M * targetDist;
    const fitted = clamp(fit, GROUND_PASS_MIN_SPEED, GROUND_PASS_MAX_SPEED);
    speed = fitted + (GROUND_PASS_MAX_SPEED - fitted) * PASS_ASSIST_CHARGE_HEADROOM * t;
  }

  resolveAim(state, striker, aimYaw, speed, lofted, assistTarget);

  const yaw = wrapAngle(datan2(aimOut.z, aimOut.x));
  const cosA = dcos(angleDeg * DEG2RAD);
  const sinA = dsin(angleDeg * DEG2RAD);
  const vx = dcos(yaw) * speed * cosA;
  const vz = dsin(yaw) * speed * cosA;
  const vy = speed * sinA;

  const b = state.ball;
  out.px = b.px;
  out.py = b.py;
  out.pz = b.pz;
  out.vx = vx;
  out.vy = vy;
  out.vz = vz;

  // Spin, mirroring applyStrike's contact-offset model. A dead ball is at rest,
  // so the impulse is BALL_MASS·v; the general form is kept anyway so a preview
  // of a moving ball stays correct.
  const impx = BALL_MASS * (vx - b.vx);
  const impy = BALL_MASS * (vy - b.vy);
  const impz = BALL_MASS * (vz - b.vz);
  const impMag = Math.sqrt(impx * impx + impy * impy + impz * impz);
  out.wx = 0; out.wy = 0; out.wz = 0;
  if (impMag > 1e-6) {
    const maxOffset = BALL_RADIUS * STRIKE_OFFSET_MAX_FRAC;
    const mag = clamp(Math.abs(backspin) * BALL_INERTIA / impMag, 0, maxOffset);
    const dy = backspin > 0 ? -mag : mag;
    // ω = (d × impulse) / I with d = (0, dy, 0). Sidespin is omitted because
    // CURVE_ENABLED is false and AI curl is hard-zero (individual.ts) — the
    // cross-product terms it would add are exactly zero today.
    out.wx = (dy * impz) / BALL_INERTIA;
    out.wy = 0;
    out.wz = (-dy * impx) / BALL_INERTIA;
  }

  out.exact = !state.config.strikeAimErrorEnabled;
  return true;
}

/** Output of `previewPassLaunch`. Structurally a `PredictLaunch` plus honesty. */
export interface PreviewLaunch {
  px: number; py: number; pz: number;
  vx: number; vy: number; vz: number;
  wx: number; wy: number; wz: number;
  /** False when the sim will roll a real aim error the preview cannot know. */
  exact: boolean;
}

/** Wide (near a touchline) and in the attacking third → treat a loft as a cross. (§4.2) */
function isCrossPosition(state: WorldState, id: number): boolean {
  const p = state.players;
  const bounds = pitchBounds(state.config);
  const wide = Math.abs(p.pz[id]!) > bounds.halfWidth * CROSS_WIDE_FRAC;
  const dir = attackDirX(state, p.team[id]!);
  const advanced = dir * p.px[id]! > bounds.halfLength * 0.5;
  return wide && advanced;
}

// ---------------------------------------------------------------------------
// Shooting (§4.3)
// ---------------------------------------------------------------------------

/**
 * Resolve a shot into the `kick` scratch.
 *   Finesse (SkillModifier): lower power, heavy sidespin → curls into the corner.
 *   Power   (Sprint):        max velocity, minimal spin, harder to place.
 * Volleys (ball above VOLLEY_HEIGHT_THRESHOLD) cost accuracy. Mouse-wheel lift
 * bias sets both the launch angle and back/topspin (float vs dip).
 */
function buildShot(state: WorldState, striker: number, input: InputCommand): void {
  const p = state.players;
  const attr = state.attributes[striker]!;

  const t = clamp01(
    (clamp(input.shootCharge, SHOT_CHARGE_MIN_S, SHOT_CHARGE_MAX_S) - SHOT_CHARGE_MIN_S) /
    (SHOT_CHARGE_MAX_S - SHOT_CHARGE_MIN_S),
  );

  const finesse = (input.flags & InputFlags.SkillModifier) !== 0;
  const power = !finesse && (input.flags & InputFlags.Sprint) !== 0;

  let speed = lerpN(SHOT_MIN_SPEED, SHOT_MAX_SPEED, t);
  let spinScale = 1;
  let sidespin = 0;
  const curve = curveGain(state);
  if (finesse) {
    speed *= FINESSE_POWER_MULTIPLIER;
    // The finesse bend is a CURVE too (measured ~1.3 m over 25 m), so it takes the
    // same gate — the power multiplier above is untouched, so a finesse shot is
    // still a placed, lower-power strike while the curve is off. (KICK DIRECTIVE)
    sidespin = curve * FINESSE_SIDESPIN * curlSign(input);
  } else if (power) {
    speed *= POWER_SHOT_MULTIPLIER;
    spinScale = POWER_SHOT_SPIN_MULTIPLIER;
  }

  // House rule: lateral mouse motion during the charge curls any shot a
  // little (input.curl ∈ [-1,1], client-accumulated). Additive on top of
  // finesse and scaled by the same spinScale so a power shot stays hard to
  // bend. Zero for AI and non-mouse inputs by contract (types.ts).
  sidespin += curve * MOUSE_CURL_SIDESPIN * clamp(input.curl, -1, 1);

  // Lift bias (§4.1): +up adds loft + backspin float, −down flattens + topspin dip.
  const lift = clamp(input.liftBias, -1, 1);
  // THE CHIP (owner, 2026-08-15: "q + mouse click ... so the ball goes from
  // above"). Q+LMB sets ThroughBall on a SHOT, which this path ignored
  // entirely - the modifier lofted passes and did nothing to strikes, so
  // half the owner's request was silently unimplemented. A chipped shot
  // leaves at CHIP_SHOT_ANGLE_DEG and takes the same charge-scaled apex floor
  // the lofted pass takes, so a dink over an advancing keeper is one gesture.
  const chipped = (input.flags & InputFlags.ThroughBall) !== 0;
  const intendedAngleDeg = chipped
    ? Math.max(CHIP_SHOT_ANGLE_DEG, Math.max(0, lift) * SHOT_LIFT_ANGLE_MAX_DEG)
    : Math.max(0, lift) * SHOT_LIFT_ANGLE_MAX_DEG;
  const backspin = lift * SHOT_LIFT_SPIN * spinScale;

  // Error cone (§4.3): base / Shooting · (1 + dist) · (1 + pressure) · (1 + offBalance),
  // widened by the volley penalty. Shooting goes through statMultiplier for the
  // same ±15% reason as passing. The cone is 2-D — one gaussian sprays the
  // bearing, an INDEPENDENT second one sprays the elevation, so a hard chance
  // misses high/scuffed as well as wide ("harder to place" is real, not just
  // lateral). A power shot widens BOTH axes: pace traded for placement.
  const bounds = pitchBounds(state.config);
  const dir = attackDirX(state, p.team[striker]!);
  const goalX = dir * bounds.goalLineX;
  const distToGoal = Math.abs(goalX - p.px[striker]!);
  const distanceFactor = 1 + distToGoal / SHOT_DISTANCE_ERROR_REF;
  const pressureFactor = 1 + pressureOn(state, striker) * PRESSURE_ERROR_WEIGHT;
  const offBalance = 1 + (playerSpeedXZ(state, striker) / MAX_SPEED_SPRINT) * SHOT_OFFBALANCE_ERROR_WEIGHT;
  const volley = state.ball.py > VOLLEY_HEIGHT_THRESHOLD ? 1 + VOLLEY_ACCURACY_PENALTY : 1;
  const errorScale =
    distanceFactor * pressureFactor * offBalance * volley *
    (power ? POWER_SHOT_ERROR_MULTIPLIER : 1);
  // ⚠ aimErrorGain is 0 under the owner's accuracy directive — see the note in
  // buildPass and the KICK DIRECTIVE block in constants.ts. The two draws still
  // happen, in this order: the gain multiplies, it never skips.
  const errGain = aimErrorGain(state);
  const errorRad =
    (SHOT_BASE_ERROR_RAD / statMultiplier(attr.shooting)) *
    errorScale * errGain * pcg32GaussianClamped(state.rng);
  const vertErrDeg =
    (SHOT_VERTICAL_ERROR_DEG / statMultiplier(attr.shooting)) *
    errorScale * errGain * pcg32GaussianClamped(state.rng);
  const angleDeg = clamp(intendedAngleDeg + vertErrDeg, SHOT_ANGLE_MIN_DEG, SHOT_ANGLE_MAX_DEG);

  const yaw = wrapAngle(input.aimYaw + errorRad);
  const cosA = dcos(angleDeg * DEG2RAD);
  const sinA = dsin(angleDeg * DEG2RAD);
  // Boost tier 2 is orthogonal to finesse/power (a player can be mid-boost
  // without holding Sprint at the moment of the strike) — applied to the
  // FINAL speed, on top of whichever branch above already ran. Same "empty
  // bar cannot boost" gate the stamina system and the boost VFX both use
  // (player-controller.ts's stepStamina, boostAura.ts's setBoostAura call).
  const boostedTier2 = (input.flags & InputFlags.Boost2) !== 0 && p.stamina[striker]! > 0;
  const finalSpeed = boostedTier2 ? speed * BOOST2_KICK_POWER_MULTIPLIER : speed;
  kick.vx = dcos(yaw) * finalSpeed * cosA;
  kick.vz = dsin(yaw) * finalSpeed * cosA;
  kick.vy = finalSpeed * sinA;
  // Same apex floor as the lofted pass — a chip that stays under the keeper's
  // hands is not a chip. Scales with the shot's own charge (`t`).
  if (chipped) {
    const wantVy = Math.sqrt(2 * -GRAVITY * lerpN(LOFT_APEX_MIN_M, LOFT_APEX_MAX_M, t));
    if (kick.vy < wantVy) kick.vy = wantVy;
  }
  kick.backspin = backspin;
  kick.sidespin = sidespin * spinScale;
  // The power-kick WAVE shows on boost-tier-2 kicks AND on an ordinary
  // full-charge kick (t hits exactly 1 — clamp01 saturates it, no epsilon
  // needed) — owner, 2026-08-15: "also on full power". Deliberately NOT the
  // same condition as the speed multiplier above: a full-power non-boosted
  // shot already IS its own maximum speed by definition, so it gets the
  // visual without a second, unrequested speed buff on top.
  kick.powerKick = boostedTier2 || t >= 1;
}

/**
 * Which way a finesse shot curls. The player leans into it: the sign of the cross
 * product of the aim direction and the movement input picks left vs right. With
 * no movement input we default to a stable +1 rather than drawing from the RNG —
 * a degenerate case should not burn the stream (cf. player-controller).
 */
function curlSign(input: InputCommand): number {
  const mmag = Math.sqrt(input.moveX * input.moveX + input.moveZ * input.moveZ);
  if (mmag < 1e-3) return 1;
  // cross(aim, move) in the XZ plane: aimx·moveZ − aimz·moveX.
  const cross = dcos(input.aimYaw) * input.moveZ - dsin(input.aimYaw) * input.moveX;
  return cross >= 0 ? 1 : -1;
}

// ---------------------------------------------------------------------------
// Headers (§4.3, house rule 2026-07-17)
// ---------------------------------------------------------------------------

/**
 * Resolve a header into the `kick` scratch. A header REDIRECTS an aerial ball
 * toward the aim: base power plus a fraction of the incoming ball's pace, capped
 * well below a footed shot. Accuracy is scaled by Heading and is deliberately
 * looser than a foot strike (HEADER_BASE_ERROR_RAD > SHOT_BASE_ERROR_RAD). It
 * carries no meaningful spin — a header is a contact, not a curl weapon.
 */
function buildHeader(state: WorldState, striker: number, input: InputCommand): void {
  const b = state.ball;
  const attr = state.attributes[striker]!;

  const incoming = Math.sqrt(b.vx * b.vx + b.vy * b.vy + b.vz * b.vz);
  let speed = (HEADER_BASE_SPEED + incoming * HEADER_REDIRECT_FRACTION) * statMultiplier(attr.heading);
  if (speed > HEADER_MAX_SPEED) speed = HEADER_MAX_SPEED;

  // Flat-ish: liftBias buys only a shallow launch angle (you cannot loft a
  // header). Negative lift is the planted DOWNWARD header toward the bottom
  // corner — legal only when the contact is genuinely above the forehead
  // (HEADER_DOWN_MIN_CONTACT_Y); a chest-high contact headed "down" just goes flat.
  const lift = clamp(input.liftBias, -1, 1);
  let intendedAngleDeg: number;
  if (lift >= 0) {
    intendedAngleDeg = lift * HEADER_LIFT_ANGLE_MAX_DEG;
  } else {
    intendedAngleDeg = b.py >= HEADER_DOWN_MIN_CONTACT_Y ? lift * HEADER_DOWN_ANGLE_MAX_DEG : 0;
  }

  // 2-D error like the foot strikes, wider: a header is the hardest strike to
  // place vertically — glanced too high or planted into the turf.
  const pressureFactor = 1 + pressureOn(state, striker) * PRESSURE_ERROR_WEIGHT;
  const errorRad =
    (HEADER_BASE_ERROR_RAD / statMultiplier(attr.heading)) *
    pressureFactor *
    pcg32GaussianClamped(state.rng);
  const vertErrDeg =
    (HEADER_VERTICAL_ERROR_DEG / statMultiplier(attr.heading)) *
    pressureFactor *
    pcg32GaussianClamped(state.rng);
  const angleDeg = clamp(
    intendedAngleDeg + vertErrDeg,
    -HEADER_DOWN_ANGLE_MAX_DEG,
    HEADER_LIFT_ANGLE_MAX_DEG + 10,
  );

  const yaw = wrapAngle(input.aimYaw + errorRad);
  const cosA = dcos(angleDeg * DEG2RAD);
  const sinA = dsin(angleDeg * DEG2RAD);
  kick.vx = dcos(yaw) * speed * cosA;
  kick.vz = dsin(yaw) * speed * cosA;
  kick.vy = speed * sinA;
  kick.backspin = 0;
  kick.sidespin = 0;
  // A header is not "kicked" — out of scope for the power bonus and the
  // red-trail VFX it drives, and explicitly cleared so an earlier power kick
  // can never leak its flag onto an unrelated header.
  kick.powerKick = false;
}

// ---------------------------------------------------------------------------
// Bicycle kick (§4.3, house rule 2026-07-17)
// ---------------------------------------------------------------------------

/**
 * Resolve a bicycle kick into the `kick` scratch. It is a normal charged shot —
 * same charge curve, same distance/pressure/off-balance error terms as buildShot,
 * so the two are directly comparable — then made more powerful (× BICYCLE_POWER_MULTIPLIER)
 * and FAR less accurate (× BICYCLE_ERROR_MULTIPLIER, the same *pattern* as the volley
 * penalty but riskier). It launches up and over (BICYCLE_BASE_ANGLE_DEG + liftBias)
 * with backspin. Spectacular, unreliable — exactly as intended.
 */
function buildBicycle(state: WorldState, striker: number, input: InputCommand): void {
  const p = state.players;
  const attr = state.attributes[striker]!;

  const t = clamp01(
    (clamp(input.shootCharge, SHOT_CHARGE_MIN_S, SHOT_CHARGE_MAX_S) - SHOT_CHARGE_MIN_S) /
    (SHOT_CHARGE_MAX_S - SHOT_CHARGE_MIN_S),
  );
  const speed = lerpN(SHOT_MIN_SPEED, SHOT_MAX_SPEED, t) * BICYCLE_POWER_MULTIPLIER;

  const bounds = pitchBounds(state.config);
  const dir = attackDirX(state, p.team[striker]!);
  const goalX = dir * bounds.goalLineX;
  const distToGoal = Math.abs(goalX - p.px[striker]!);
  const distanceFactor = 1 + distToGoal / SHOT_DISTANCE_ERROR_REF;
  const pressureFactor = 1 + pressureOn(state, striker) * PRESSURE_ERROR_WEIGHT;
  const offBalance = 1 + (playerSpeedXZ(state, striker) / MAX_SPEED_SPRINT) * SHOT_OFFBALANCE_ERROR_WEIGHT;
  const bicycleErrScale =
    distanceFactor * pressureFactor * offBalance * BICYCLE_ERROR_MULTIPLIER;
  const errorRad =
    (SHOT_BASE_ERROR_RAD / statMultiplier(attr.shooting)) *
    bicycleErrScale * pcg32GaussianClamped(state.rng);
  // 2-D like buildShot — an overhead strike sprays vertically hardest of all.
  const vertErrDeg =
    (SHOT_VERTICAL_ERROR_DEG / statMultiplier(attr.shooting)) *
    bicycleErrScale * pcg32GaussianClamped(state.rng);

  const lift = clamp(input.liftBias, -1, 1);
  const angleDeg = clamp(
    BICYCLE_BASE_ANGLE_DEG + Math.max(0, lift) * SHOT_LIFT_ANGLE_MAX_DEG + vertErrDeg,
    SHOT_ANGLE_MIN_DEG,
    SHOT_ANGLE_MAX_DEG + BICYCLE_BASE_ANGLE_DEG,
  );

  const yaw = wrapAngle(input.aimYaw + errorRad);
  const cosA = dcos(angleDeg * DEG2RAD);
  const sinA = dsin(angleDeg * DEG2RAD);
  kick.vx = dcos(yaw) * speed * cosA;
  kick.vz = dsin(yaw) * speed * cosA;
  kick.vy = speed * sinA;
  kick.backspin = BICYCLE_BACKSPIN;
  kick.sidespin = 0;
  // Already "made more powerful" on its own terms (BICYCLE_POWER_MULTIPLIER)
  // — out of scope for the power bonus/red-trail VFX, and cleared for the
  // same stale-flag reason buildHeader clears it.
  kick.powerKick = false;
}

/**
 * Put the bicycle kicker on the floor after the strike. The strike frame shows
 * AnimState.BicycleKick; from the next tick stepAnim reads knockdownTimer and
 * shows Fallen. We use knockdownTimer — the same "planted, unsteerable, gates
 * every follow-up action" field a punch/kick victim gets (player-controller.ts) —
 * rather than a bare actionTimer, because only that yields the specified
 * on-the-ground Fallen recovery (actionTimer alone would leave him steerable).
 * Horizontal velocity is zeroed so he does not run out of his own kick.
 */
function startBicycleRecovery(state: WorldState, id: number): void {
  const p = state.players;
  p.vx[id] = 0;
  p.vz[id] = 0;
  p.knockdownTimer[id] = BICYCLE_RECOVERY_S;
  p.skillActive[id] = SkillMove.None;
  p.skillTimer[id] = 0;
  beginAnimAction(p, id, AnimState.BicycleKick);
}

// ---------------------------------------------------------------------------
// The strike itself (§3.2) — impulse + Δω = (d × impulse)/I
// ---------------------------------------------------------------------------

/**
 * Apply the resolved `kick` to the ball as an impulse at an offset strike point,
 * so the spin emerges from where the ball is hit rather than being pasted on.
 *
 * impulse = m·(targetVel − vBefore). The strike offset `d` is back-solved from the
 * intended spin (|d| = |ω|·I/|impulse|, clamped to a fraction of the radius) and
 * pointed to produce that spin: low/high on the ball for back/topspin, off to the
 * side for sidespin. Then Δω = (d × impulse)/I recovers the spin — exactly, until
 * the clamp bites on a weak kick, which is precisely when a real player can't put
 * much on it either.
 */
function applyStrike(state: WorldState, striker: number, action = 'strike'): void {
  const p = state.players;
  const b = state.ball;

  const impx = BALL_MASS * (kick.vx - b.vx);
  const impy = BALL_MASS * (kick.vy - b.vy);
  const impz = BALL_MASS * (kick.vz - b.vz);
  const impMag = Math.sqrt(impx * impx + impy * impy + impz * impz);

  const wasPossessed = b.possessedBy === striker;
  const tvx0 = b.vx, tvy0 = b.vy, tvz0 = b.vz;
  // Set the linear velocity — Δv = impulse/m, applied in full.
  b.vx = kick.vx;
  b.vy = kick.vy;
  b.vz = kick.vz;
  b.wx = 0; b.wy = 0; b.wz = 0;
  // Overwritten on EVERY strike, power or not — see BallState.powerKick.
  b.powerKick = kick.powerKick;
  traceBallSince(state, BallVelocityCause.Kick, striker, tvx0, tvy0, tvz0);
  // Contact-truth record: the strike geometry AT contact, before integration.
  if (contactLogOn()) {
    logContact({
      tick: state.tick,
      kind: 'kick',
      player: striker,
      action,
      contact: footContact(p.px[striker]!, p.pz[striker]!, p.py[striker]!, b.px, b.py, b.pz),
      ballBefore: [tvx0, tvy0, tvz0],
      ballAfter: [b.vx, b.vy, b.vz],
      deltaV: impMag / BALL_MASS,
      possessed: wasPossessed,
    });
  }

  if (impMag > 1e-6) {
    const maxOffset = BALL_RADIUS * STRIKE_OFFSET_MAX_FRAC;

    // Horizontal launch direction, for orienting the strike offsets.
    const hSpeed = Math.sqrt(kick.vx * kick.vx + kick.vz * kick.vz);
    let dirx = 1;
    let dirz = 0;
    if (hSpeed > 1e-6) {
      dirx = kick.vx / hSpeed;
      dirz = kick.vz / hSpeed;
    }

    // Back/topspin → hit low (dy<0 = backspin) or high (dy>0 = topspin). The
    // vertical offset back-solved from the intended magnitude.
    let dy = 0;
    if (kick.backspin !== 0) {
      const mag = clamp(Math.abs(kick.backspin) * BALL_INERTIA / impMag, 0, maxOffset);
      dy = kick.backspin > 0 ? -mag : mag;
    }

    // Sidespin → hit off to the side. Lateral offset is perpendicular to the
    // launch direction in the XZ plane.
    let dlatx = 0;
    let dlatz = 0;
    if (kick.sidespin !== 0) {
      const mag = clamp(Math.abs(kick.sidespin) * BALL_INERTIA / impMag, 0, maxOffset);
      const s = kick.sidespin > 0 ? mag : -mag;
      // Left-perpendicular of (dirx,dirz) in XZ is (-dirz, dirx).
      dlatx = -dirz * s;
      dlatz = dirx * s;
    }

    const dxv = dlatx;
    const dyv = dy;
    const dzv = dlatz;

    // Δω = (d × impulse) / I.
    b.wx = (dyv * impz - dzv * impy) / BALL_INERTIA;
    b.wy = (dzv * impx - dxv * impz) / BALL_INERTIA;
    b.wz = (dxv * impy - dyv * impx) / BALL_INERTIA;
    clampSpin(b);
  }

  // The ball has left the foot. (§3.4 — no ball-glue, ever.)
  b.grounded = kick.vy > 0 ? 0 : b.grounded;
  b.possessedBy = -1;
  b.gatherOwner = -1; // §1 a KICK disables the gather — the ball is played, let it fly free.
  b.lastTouchedBy = striker;
  b.lastTouchedTeam = p.team[striker]!;
  b.lastTouchTick = state.tick;
  for (let i = 0; i < MAX_PLAYERS; i++) p.possessing[i] = 0;
}

/** Cap |ω| — elite players reach ~10 rev/s. (§3.2) */
function clampSpin(b: WorldState['ball']): void {
  const wsq = b.wx * b.wx + b.wy * b.wy + b.wz * b.wz;
  const maxSq = BALL_MAX_ANGULAR_VELOCITY * BALL_MAX_ANGULAR_VELOCITY;
  if (wsq > maxSq && wsq > 0) {
    const s = BALL_MAX_ANGULAR_VELOCITY / Math.sqrt(wsq);
    b.wx *= s; b.wy *= s; b.wz *= s;
  }
}

// ===========================================================================
// stepBall (§3.2 aero, §3.4 dribble touch + ground contact) — stage 7
// ===========================================================================

/**
 * Advance the ball one tick: dribble touch, gravity, quadratic drag (with the
 * drag crisis), Magnus lift, spin decay, and ground/obstacle contact.
 */
export function stepBall(
  state: WorldState,
  inputs: ReadonlyArray<InputCommand | null>,
  physics: PhysicsWorld,
): void {
  const b = state.ball;

  // A ball in a keeper's gloves is his entirely — he set its transform at stage
  // 5; integrating it here would pull it out of his hands. (§4.5)
  const holder = b.possessedBy;
  if (holder >= 0 && state.players.gkHoldStartTick[holder]! >= 0) return;

  // Dribble touch model (§3.4): keep a possessed ball at the dribbler's feet.
  // The carrier's own input steers the touch (see applyDribbleTouch). When a
  // knock-and-run runs the ball just past the capture gate the holder is briefly
  // -1, so also run it for the GATHER owner (§1) — that is what lets the touch
  // pull an escaping knock back instead of never firing once possession blips. A
  // KICK clears gatherOwner, so a played ball is not chased by this. Only in live
  // play: a loose ball at a restart belongs to the rules layer, not its former
  // carrier (applyDribbleTouch's gather is itself InPlay-gated as a backstop).
  const dribbler =
    holder >= 0 ? holder : (state.match.phase === MatchPhase.InPlay ? b.gatherOwner : -1);
  if (dribbler >= 0) applyDribbleTouch(state, dribbler, inputs[dribbler] ?? null);
  else if (state.match.phase === MatchPhase.InPlay) applyFirstTouchTrap(state);

  integrateAero(state);

  // Spin decay in air (§3.2 pt 4). Ground contact adds much faster decay below.
  const airDecay = 1 - BALL_SPIN_DECAY_AIR * FIXED_DT;
  b.wx *= airDecay; b.wy *= airDecay; b.wz *= airDecay;

  // Position: swept when fast (CCD, §3.4) so the ball cannot tunnel a net or a
  // foot, otherwise a plain step. A hit stops the ball at the surface and reflects
  // it; the contact pass below then only has resting/penetration to settle.
  const dx = b.vx * FIXED_DT;
  const dy = b.vy * FIXED_DT;
  const dz = b.vz * FIXED_DT;
  const disp = Math.sqrt(dx * dx + dy * dy + dz * dz);
  let sweptResolved = false;
  if (disp > BALL_RADIUS) {
    // The sweep primitive itself excludes the pitch slab when the ball starts
    // FLUSH on it and is not moving downward (the phantom-ricochet fix — see
    // sweepBall in physics-rapier.ts), so any hit reported here is a genuine
    // surface with a trustworthy toi, kind and normal.
    // The carrier's own capsule is excluded while he possesses the ball: his
    // feet shepherd it (the touch model), they do not wall it off. (§3.4)
    physics.sweepBall(b.px, b.py, b.pz, dx, dy, dz, sweepOut, b.possessedBy);
    if (sweepOut.hit && sweepOut.toi < 1) {
      b.px += dx * sweepOut.toi + sweepOut.nx * BALL_CONTACT_SKIN;
      b.py += dy * sweepOut.toi + sweepOut.ny * BALL_CONTACT_SKIN;
      b.pz += dz * sweepOut.toi + sweepOut.nz * BALL_CONTACT_SKIN;
      resolveSurface(state, sweepOut.nx, sweepOut.ny, sweepOut.nz, sweepOut.kind);
      sweptResolved = true;
    }
  }
  if (!sweptResolved) {
    b.px += dx; b.py += dy; b.pz += dz;
  }

  integrateSpinQuat(state);

  // Push POD state into Rapier, read contacts back, resolve them. (D1)
  physics.syncAndStep(state);
  const contacts = physics.getBallContacts();
  b.grounded = 0;
  for (let i = 0; i < contacts.length; i++) {
    const c = contacts[i]!;
    if (c.kind === ContactKind.None || c.kind === ContactKind.Boundary) continue; // rules/ owns out-of-play
    // The CARRIER's body never stomps his own ball: a capsule whose bottom
    // hemisphere overlaps a ball rolling between the feet reports a DOWNWARD
    // normal (measured ny −0.5..−1.0, depth 0.11) and the depenetration push +
    // bounce pinned the carried ball under the boot — the sprint-carry swallow.
    // His interaction with the ball he possesses is the touch model (§3.4);
    // every other player's body remains a real obstacle (blocks, tackles).
    if (c.kind === ContactKind.Player && c.playerId === b.possessedBy) continue;
    // Push out of penetration along the contact normal.
    if (c.depth > 0) {
      b.px += c.nx * c.depth;
      b.py += c.ny * c.depth;
      b.pz += c.nz * c.depth;
    }
    if (!sweptResolved) resolveSurface(state, c.nx, c.ny, c.nz, c.kind);
    // Head-rest destabilizer (see constants.ts BALL_HEADREST_*): a slow ball
    // sitting ON TOP of a capsule is a stable equilibrium the solver will hold
    // forever, and it deadlocks the match — nobody can possess a 1.9m ball and
    // the AI under it stands still. Shove it horizontally off the axis; the
    // direction comes from the contact normal's horizontal part, falling back
    // to the player→ball offset, then to +x (fixed, deterministic).
    //
    // TWO-HEAD WEDGE (observed live after the single-head fix): a ball resting
    // in the V between TWO adjacent players gets two opposing radial shoves
    // that cancel. Each shove is therefore ROTATED off the pure radial by a
    // fixed angle whose SIGN comes from the contact player's slot parity —
    // mirrored contacts then push with a common tangential component instead
    // of annihilating, and the ball corkscrews out of the wedge. Deterministic:
    // parity is static roster data, the angle a constant.
    // HEIGHT gate (fixes the "ball glitches near players' feet"): only a ball
    // genuinely resting on the capsule TOP can deadlock — a slow ball touching
    // a boot/shin (py ≈ 0.1-0.5) must NOT get shoved, or it jitters every tick.
    // A head-rest sits near capsule height; require the ball clearly above knee.
    if (
      c.kind === ContactKind.Player &&
      c.ny > BALL_HEADREST_NY_MIN &&
      b.py > BALL_HEADREST_MIN_Y
    ) {
      const sp = Math.sqrt(b.vx * b.vx + b.vz * b.vz);
      if (sp < BALL_HEADREST_MAX_SPEED) {
        let hx = c.nx;
        let hz = c.nz;
        let hlen = Math.sqrt(hx * hx + hz * hz);
        if (hlen < 1e-4 && c.playerId >= 0) {
          hx = b.px - state.players.px[c.playerId]!;
          hz = b.pz - state.players.pz[c.playerId]!;
          hlen = Math.sqrt(hx * hx + hz * hz);
        }
        if (hlen < 1e-4) {
          hx = 1; hz = 0; hlen = 1;
        }
        const rot = (c.playerId >= 0 && c.playerId % 2 === 0 ? 1 : -1) * BALL_HEADREST_ROT_RAD;
        const cr = dcos(rot);
        const sr = dsin(rot);
        const dx = (hx * cr - hz * sr) / hlen;
        const dz = (hx * sr + hz * cr) / hlen;
        const hvx0 = b.vx, hvy0 = b.vy, hvz0 = b.vz;
        b.vx += dx * BALL_HEADREST_SHOVE;
        b.vz += dz * BALL_HEADREST_SHOVE;
        traceBallSince(state, BallVelocityCause.PlayerBodyContact, c.playerId, hvx0, hvy0, hvz0);
      }
    }
  }

  // Rolling resistance + fast ground spin decay on a settled ball. (§3.2 pt 5)
  if (b.grounded === 1) applyGroundRoll(state);

  // ── Dead-ball out-of-play handling (Phase-1 Step 6) ─────────────────────
  // The pitch boundary is a rules boundary; there are no physical walls. A
  // LIVE ball that fully crosses a line is collected by the FSM the same tick
  // (evaluateBallOut), but in phases where the rules never collect it (Goal
  // dwell, HalfTime, FullTime) stage-7 integration would carry it forever.
  // Once the whole ball is clearly out (line + margin), damp it to rest —
  // horizontal only, so a lofted ball still lands under gravity. Nothing ever
  // pushes it back toward the pitch.
  if (state.match.phase !== MatchPhase.InPlay) {
    const bounds = pitchBounds(state.config);
    const outX = Math.abs(b.px) - BALL_RADIUS - bounds.halfLength;
    const outZ = Math.abs(b.pz) - BALL_RADIUS - bounds.halfWidth;
    if (outX > DEAD_BALL_OUT_MARGIN || outZ > DEAD_BALL_OUT_MARGIN) {
      const damp = 1 - DEAD_BALL_DAMP_PER_S * FIXED_DT;
      const dvx0 = b.vx, dvy0 = b.vy, dvz0 = b.vz;
      b.vx *= damp;
      b.vz *= damp;
      traceBallSince(state, BallVelocityCause.RuleRestart, -1, dvx0, dvy0, dvz0);
    }
  }

  // ── Containment failsafe ─────────────────────────────────────────────────
  // Should be dead code: a live out-of-play ball is collected by the FSM the
  // same tick, and a dead-phase runaway is damped to rest above (Step 6 — the
  // world walls are gone; the boundary is rules-owned). It exists because
  // "should" has already lost once: any future physics regression must degrade
  // to a clamped ball at the world edge — never to a ball at y=-4000 falling
  // out of a live match. Positions outside the §2.8 quantization volume cannot
  // even be encoded on the wire, so this bound is not a taste decision; it is
  // the format's own invariant. It ZEROES velocity — never reflects — so it
  // cannot masquerade as a wall. Pure arithmetic, deterministic, ~free.
  const margin = BALL_RADIUS + 1;
  const maxX = QUANT_POS_MAX_X - margin;
  const maxZ = QUANT_POS_MAX_Z - margin;
  if (b.px > maxX) { b.px = maxX; if (b.vx > 0) b.vx = 0; }
  else if (b.px < -maxX) { b.px = -maxX; if (b.vx < 0) b.vx = 0; }
  if (b.pz > maxZ) { b.pz = maxZ; if (b.vz > 0) b.vz = 0; }
  else if (b.pz < -maxZ) { b.pz = -maxZ; if (b.vz < 0) b.vz = 0; }
  if (b.py < BALL_RADIUS - 0.5) { b.py = BALL_RADIUS; if (b.vy < 0) b.vy = 0; b.grounded = 1; }
  else if (b.py > QUANT_POS_MAX_Y - BALL_RADIUS) {
    // §2.8 encoding-ceiling failsafe — genuinely dead code since Phase-1 Step 7
    // widened QUANT_POS_MAX_Y 10 → 50: the highest realistic trajectory (a
    // full-power loft, ~17 m peak measured) has ≈2.9× headroom, so no football
    // flight can reach 49.89 m. Kept as the format's last-resort invariant only
    // (positions past the ceiling cannot go on the wire); a normal trajectory
    // is NEVER clamped here — boundary-behavior Probe 3 asserts sim ≡ ghost.
    b.py = QUANT_POS_MAX_Y - BALL_RADIUS;
    if (b.vy > 0) b.vy = 0;
  }
}

/**
 * Dribble touch model (§3.4). Keep a possessed ball AT THE FEET while the carrier
 * moves: the ball wants to sit a short lead ahead of the feet in the movement
 * direction, travelling at the carrier's pace. The touch is bigger at speed (the
 * ball rides further ahead → less control) and tighter with Dribbling. No
 * ball-glue: this is a capped impulse toward a want-velocity, and the ball rolls,
 * bounces and can be tackled away freely between and during touches.
 *
 * ── Why this was rewritten (user 2026-07-18: "the ball is not following the taps
 *    ... it doesn't stay with the moving player") ────────────────────────────
 *
 * The old model aimed the ball at `pspeed + touch` in the movement direction —
 * a VELOCITY target that ALWAYS exceeded the carrier's own pace by the full touch
 * impulse (2.2–3.5 m/s). So every touch knocked the ball away faster than the
 * carrier could follow, and with rolling resistance at only 0.02 (§3.2) it then
 * rolled clear and possession dropped. Measured (dribble-follow.test.ts): a
 * straight sprint held possession 5.7% of ticks, ball a median 5.6 m off the feet.
 * A pure velocity target cannot hold a POSITION — that was the whole bug.
 *
 * The fix keeps the exact shape §3.4 asks for (accelerate toward a want-velocity,
 * capped per tick) but retargets the want-velocity: travel WITH the player, plus
 * a spring that pulls the ball onto a lead point just ahead of the feet. Steady
 * state is the ball sitting a lead-distance ahead at the carrier's speed — the
 * feet — instead of being flung away.
 *
 * The touch fires on the ball's distance from that moving LEAD POINT, not from the
 * player: a ball within DRIBBLE_SETTLE_RADIUS of where it wants to be is under
 * control and rolls free; past it, the touch brings it back. Gating on the lead
 * point (not raw player-to-ball distance, as the first rewrite did) is what makes
 * a STANDING START work — a dead ball at the feet is "close to the player" yet far
 * from the lead point that runs ahead as you set off, so it gets knocked into
 * stride instead of being left where it lay. Beyond DRIBBLE_REACH the ball is
 * genuinely getting away: no touch, it keeps its own momentum (chase it).
 */
/**
 * First-touch trap (§ receiving). A loose ball moving faster than the capture
 * gate and heading roughly AT a nearby player gets a cushioning touch that bleeds
 * its speed toward a controllable pace and makes that player the gather owner, so
 * the NEXT tick's control envelope (updatePossession) captures it — possession on
 * the touch, not after the ball rolls to a near-stop. A physical velocity damp
 * (capped per tick, modulated by the firstTouch stat), never a teleport. Runs only
 * for a genuinely loose ball in live play; tightly gated (speed > gate, inside
 * FIRST_TOUCH_RADIUS, aligned, off cooldown) so it is not a magnet.
 */
function applyFirstTouchTrap(state: WorldState): void {
  const b = state.ball;
  const p = state.players;
  // ⚠ THE CEILING IS THE CHEST NOW, NOT THE THIGH. POSSESSION_MAX_HEIGHT (1.0,
  // "knee/thigh; a header is not control") is the height at which the ordinary
  // possession gate collects a ball, and it also silently capped the TRAP — so
  // a ball dropping onto a player's chest was neither collected nor cushioned
  // and simply bounced off him. The owner asked for that ball to be taken on
  // the chest and let down to the feet, so the trap now reaches
  // CHEST_CONTROL_MAX_HEIGHT (1.55), which stops below HEADER_CONTACT_HEIGHT
  // (1.6) so a ball is never both a header and a trap.
  if (b.possessedBy >= 0 || b.py > CHEST_CONTROL_MAX_HEIGHT) return;
  if (Math.sqrt(b.vx * b.vx + b.vz * b.vz) > FIRST_TOUCH_MAX_SPEED) return; // untrappable rocket

  // A6 §6: the trap window and alignment are RELATIVE to each candidate — the
  // cushion math below always was. The old ABSOLUTE gates left a dead zone: a
  // ball at 0–8 m/s met by a SPRINTING player was neither capturable (relative
  // speed above the possession gate) nor cushioned (ball under the absolute
  // floor), so it just bounced off his capsule at restitution 0.8 — the "fast
  // player runs straight through / punts the loose ball" defect. In the
  // player's own frame that ball arrives fast and head-on; gating on relative
  // motion is the identical physical statement for a stationary receiver
  // (relative == absolute there, so the classic fast-pass cushion is
  // unchanged) and generalizes to the RUNNING receive.
  let best = -1;
  let bestDist = FIRST_TOUCH_RADIUS;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    // The striker can't instantly re-trap his own just-played ball.
    if (i === b.lastTouchedBy && state.tick - b.lastTouchTick < FIRST_TOUCH_COOLDOWN_TICKS) continue;
    const toX = p.px[i]! - b.px;
    const toZ = p.pz[i]! - b.pz;
    const d = Math.sqrt(toX * toX + toZ * toZ);
    if (d > bestDist || d < 1e-4) continue;
    // Ball motion in the candidate's frame: it must EXCEED the possession
    // capture gate (else the ordinary gate already collects it untouched),
    // stay under the trap ceiling (too hot to cushion), and be closing within
    // ~44° of him (not merely passing nearby).
    const rvx = b.vx - p.vx[i]!;
    const rvz = b.vz - p.vz[i]!;
    const rs = Math.sqrt(rvx * rvx + rvz * rvz);
    if (rs <= POSSESSION_CAPTURE_REL_SPEED || rs > FIRST_TOUCH_MAX_SPEED) continue;
    const ballSpeed = Math.sqrt(b.vx * b.vx + b.vz * b.vz);
    // ── THE 5-8 m/s RECEIVE DEAD ZONE, closed (§ receiving) ─────────────────
    // There used to be an outright `continue` here for the case "neither the
    // ball nor the receiver is fast": ballSpeed <= FIRST_TOUCH_MIN_SPEED (8)
    // AND pSpeed <= POSSESSION_CAPTURE_REL_SPEED (5). Combined with the gate
    // two lines above (rs must EXCEED 5 to be here at all) that carved out a
    // hole: a ball reaching a STANDING receiver at 5-8 m/s was above the
    // possession capture gate, so `updatePossession` refused it, and below the
    // trap's cause gate, so nothing cushioned it either. It simply struck his
    // capsule and bounced — "the pass bounced off my player".
    //
    // The hole was invisible while the charge bar was worth ±15 %, because no
    // assisted pass landed in it. Giving the bar real power put passes there
    // immediately: MEASURED on pass-quality's `first-time` cell at mid charge,
    // arrival went 4.9 → 6.9 m/s and the receiver stopped collecting it (3/3 →
    // 0/3), and the same happened to d6/d12-jog-away at full charge.
    //
    // The reason the skip was added is real and is HONOURED, just by a
    // different line: with the full 1.7 m trap radius a 5-8 m/s roll at a
    // standing man was cushioned an arm's length out and died 1.1 m short of
    // his feet (measured). That is a REACH problem, and the reach line below
    // already solves it — a ball under FIRST_TOUCH_MIN_SPEED is a boot-reach
    // touch (BALL_PLAY_REACH, 0.7 m), not an as-it-arrives cushion. So the
    // marginal ball is now taken, but only once it is genuinely at his feet,
    // which is where a real player takes it.
    //
    // Reach depends on the CAUSE: cushioning a genuinely fast incoming ball is
    // an as-it-arrives touch (full trap radius) — but anything slower is a
    // boot-reach event. Giving the slow case a wider radius turned every AI
    // 50/50 into a guaranteed collection and pinned match possession ~96/4
    // (measured, seed sweep) — 0.7 m keeps the human-facing fix (the capsule
    // bounce happens at 0.41 m) without rewriting loose-ball contests.
    //
    // ⚠ THIS LINE IS LOAD-BEARING FOR THE WHOLE MATCH, not just for receiving.
    // Widening it to 1.15 m for the band re-admitted above was tried and
    // REVERTED on evidence: over eight seeded 120 s AI-vs-AI matches, ball
    // ownership went from 51-80 % of live play to 84-89 % on two of the eight
    // and on-ball decisions collapsed from 572/559 to 89/55 — a possession
    // LOCK, the exact failure this comment already warned about. The receive
    // problem is fixed by cushioning HARDER at the same reach
    // (FIRST_TOUCH_MAX_DV), which changes the quality of the touch and not
    // which balls a player can reach.
    if (ballSpeed <= FIRST_TOUCH_MIN_SPEED && d > BALL_PLAY_REACH) continue;
    const align = (rvx * toX + rvz * toZ) / (rs * d);
    if (align < FIRST_TOUCH_ALIGN_MIN) continue;
    bestDist = d;
    best = i;
  }
  if (best < 0) return;

  // Cushion: bleed the ball's velocity RELATIVE to the receiver down to a
  // controllable pace, keeping its direction, capped per tick (a harder ball
  // takes an extra tick or two — a difficult touch). firstTouch improves it.
  const relVx = b.vx - p.vx[best]!;
  const relVz = b.vz - p.vz[best]!;
  const relSpeed = Math.sqrt(relVx * relVx + relVz * relVz);
  if (relSpeed <= FIRST_TOUCH_TARGET_REL_SPEED) return;
  // Bleed the ball's speed RELATIVE to the receiver toward a trappable pace,
  // keeping direction, capped per tick. We do NOT claim possession here — the
  // cushioned (slower) ball rolls the last step to his feet and the ordinary
  // capture gate collects it there, just far sooner than a full-pace ball that
  // has to friction-decelerate first. This keeps the ball arriving AT the feet
  // (a light touch, not a claim at arm's length) while making a fast pass
  // controllable the moment it reaches him.
  // ── WHICH SURFACE, AND HOW WELL (owner: chest/knee receives) ────────────
  //
  // "when ball is at chest height and player receive it show how real ball
  // deflect with the intensity and if the intensity is low drop the ball on
  // ground as per physics and player can continue from there / and if the ball
  // is at knee height same receive and pass to foot".
  //
  // The touch used to be horizontal-only: it bled vx/vz toward the receiver's
  // frame and NEVER TOUCHED b.vy. So a ball taken on the chest kept its bounce
  // and carried on in the direction it arrived, merely slower — which is the
  // opposite of a chest control, where the whole point is to take the pace off
  // and put the ball DOWN at your feet.
  //
  // `quality` is the intensity read the owner asked for: a gentle ball met by
  // a composed player cushions dead, a rocket sprays off the surface. It is
  // the same 0..1 on both surfaces; only the drop speed differs, because a
  // knee has further to drop the ball than a chest does.
  const surface = b.py >= CHEST_CONTROL_MIN_HEIGHT ? 2
    : b.py >= KNEE_CONTROL_MIN_HEIGHT ? 1 : 0;
  const quality = surface === 0 ? 0 : clamp01(
    (1 - relSpeed / FIRST_TOUCH_MAX_SPEED) * statMultiplier(state.attributes[best]!.firstTouch),
  );
  // A cushioned ball is deadened harder than a footed touch bleeds it; a
  // badly-met one keeps its pace. Footed touches (surface 0) are unchanged.
  const targetRel = FIRST_TOUCH_TARGET_REL_SPEED * (1 - quality * CONTROL_DEADEN);
  const scale = targetRel / relSpeed;
  const wantVx = p.vx[best]! + relVx * scale;
  const wantVz = p.vz[best]! + relVz * scale;
  const dvx = wantVx - b.vx;
  const dvz = wantVz - b.vz;
  const dvMag = Math.sqrt(dvx * dvx + dvz * dvz);
  const maxDv = FIRST_TOUCH_MAX_DV * statMultiplier(state.attributes[best]!.firstTouch) * FIXED_DT;
  const k = dvMag <= maxDv ? 1 : maxDv / dvMag;
  const bx0 = b.vx, by0 = b.vy, bz0 = b.vz;
  b.vx += dvx * k;
  b.vz += dvz * k;
  // ── AND PUT IT DOWN ──────────────────────────────────────────────────────
  // The vertical half, which did not exist. A well-cushioned chest or knee
  // control kills the bounce and drives the ball toward the ground so it lands
  // at his feet and he plays on from there — exactly the owner's "if the
  // intensity is low drop the ball on ground as per physics and player can
  // continue from there". A poor one leaves the bounce in and the ball
  // deflects away, which is what makes a heavy ball hard to take.
  if (surface > 0) {
    const drop = (surface === 2 ? CHEST_CONTROL_DROP_SPEED : KNEE_CONTROL_DROP_SPEED) * quality;
    b.vy = b.vy * (1 - quality) - drop;
    b.grounded = 0; // it is falling, not rolling — physics owns it from here
  }
  traceBallSince(state, BallVelocityCause.FootTouch, best, bx0, by0, bz0);

  // SHOW THE TOUCH. This function cushioned the ball and published NO
  // ANIMATION AT ALL, so a trap read as the ball deciding by itself to stop
  // dead at a player's feet — the owner's "receive the ball with knee /
  // chest" is the same defect seen from the other side. The surface follows
  // the height, and a footed touch keeps the gait it already had (a player
  // does not stop running to take a rolling ball on the laces).
  if (b.py >= CHEST_CONTROL_MIN_HEIGHT) {
    beginAnimAction(p, best, AnimState.ChestControl);
  } else if (b.py >= KNEE_CONTROL_MIN_HEIGHT) {
    beginAnimAction(p, best, AnimState.KneeControl);
  }
}

/**
 * Which boot the ball is being carried on right now, as a signed offset across
 * the running line (+ = left of `dir`, the same sign convention as the knock's
 * `lat`). See the DRIBBLE_FOOT_OFFSET block in constants.ts for why.
 *
 * Returns 0 below DRIBBLE_WEAVE_MIN_SPEED, which restores the old dead-centre
 * target exactly — so a standing start, a walk and the tuned corner case are
 * bit-for-bit unchanged.
 *
 * DETERMINISM (§15.3): the phase is a function of `state.tick` and the player
 * id. No wall clock, no RNG, no accumulated float state — a re-simulated tick
 * produces the identical offset, which is what reconciliation requires. The id
 * term is a half-period stagger so two team-mates running together do not weave
 * in lockstep (`id * 0.5` and not `id * 0.37`, because a rational stagger keeps
 * the sequence period bounded and inspectable).
 */
function dribbleWeaveLat(state: WorldState, id: number, pspeed: number): number {
  if (pspeed < DRIBBLE_WEAVE_MIN_SPEED) return 0;
  const ramp = clamp01((pspeed - DRIBBLE_WEAVE_MIN_SPEED) / DRIBBLE_WEAVE_RAMP_SPEED);
  const phase = Math.floor(state.tick * FIXED_DT / DRIBBLE_WEAVE_HALF_PERIOD_S + id * 0.5) % 2;
  return (phase === 0 ? 1 : -1) * DRIBBLE_FOOT_OFFSET * ramp;
}

function applyDribbleTouch(state: WorldState, id: number, input: InputCommand | null): void {
  const p = state.players;
  const b = state.ball;

  const px = p.px[id]!;
  const pz = p.pz[id]!;

  // §1 GATHER touch — a controlled dribble must not run past the control envelope.
  // ONLY while this player has controlled possession (a kick clears possessedBy in
  // resolveKicks the tick before, so the gather is dead the instant the ball is
  // played; a loose/contested/tackled ball is not possessed either). As the ball
  // nears DRIBBLE_GATHER_RADIUS the carrier checks its pace: bleed the AWAY
  // component of its velocity (a foot check), scaled 0→full from DRIBBLE_GATHER_
  // START to the boundary. This only nudges VELOCITY — never sets position, never
  // parents the ball, never pushes it TOWARD the carrier (so it can't be pulled
  // through another player), and it leaves spin/rotation untouched. A defender who
  // reaches the ball still wins/displaces it; this just eases a runaway back into
  // control. Runs BEFORE the foot-reach gate so it can act while the ball is ahead.
  // Fires while this player OWNS the gather — either controlling now, or through
  // the brief blip where a knock ran the ball just past the capture gate (best
  // flipped to -1 for a few ticks but gatherOwner is still him). Keying on
  // gatherOwner, not live possession, is what lets the touch pull a knocked ball
  // back instead of dying the instant it escapes. A KICK cleared gatherOwner in
  // resolveKicks last stage → the gather is dead the moment the ball is played;
  // a rival winning it took ownership → the ex-carrier no longer gathers.
  // Live play only: during a restart the ball belongs to the rules layer (placed
  // at a spot, thrown or kicked in), so the controlled-dribble gather must not
  // touch it — even though gatherOwner may still name the pre-stoppage carrier.
  // ⚠ AND HE MUST ACTUALLY BE CARRYING IT (owner, from a screenshot of a
  // STANDING player with the ball a metre away: "ball is moving from this
  // distance").
  //
  // The gather exists to stop a ball RUNNING AWAY FROM A MOVING CARRIER — every
  // comment below is about a carry, a knock-and-run, a turn. A stationary
  // player has no carry to protect, and applying it to him is pure telekinesis:
  // MEASURED, a standing player dragged a dead ball 1.29 m at ~2 m/s from a
  // metre away, with no boot within 0.6 m of it. Worse, it was INVERTED — at
  // 0.40 m, the one distance he could really kick it, the ball sits inside the
  // settle band and nothing happens; every distance he cannot reach, it moved.
  //
  // The trace is what found it: 30-41 x FootTouch per half-second on a ball
  // nobody was touching. The gather starts at DRIBBLE_GATHER_START (0.6 m) and
  // reaches to DRIBBLE_GATHER_MAX_DIST (2.9 m) and records as a foot touch, so
  // it reads on screen as a boot playing a ball that is not there.
  //
  // Gated on the CARRIER's pace, not the ball's: a man standing over his ball
  // keeps it (ownership is POSSESSION_CONTROL_RADIUS's job, untouched here),
  // he simply stops reeling it in from across the six-yard box.
  const gatherCarrySpeed = Math.sqrt(p.vx[id]! * p.vx[id]! + p.vz[id]! * p.vz[id]!);
  const gatherActive =
    state.match.phase === MatchPhase.InPlay &&
    gatherCarrySpeed >= DRIBBLE_GATHER_MIN_CARRY_SPEED &&
    b.gatherOwner === id && (b.possessedBy === id || b.possessedBy < 0);
  if (gatherActive) {
    const gdx = b.px - px;
    const gdz = b.pz - pz;
    const gdist = Math.sqrt(gdx * gdx + gdz * gdz);
    // A ball moving fast relative to the owner is a PLAYED ball (pass/shot/heavy
    // deflection), not a dribble touch — the gather leaves it alone. A kick already
    // clears gatherOwner, so this is a backstop for any loose-but-owned ball that
    // was launched without going through applyStrike.
    const gRelSpeed = Math.hypot(b.vx - p.vx[id]!, b.vz - p.vz[id]!);
    // Past DRIBBLE_GATHER_MAX_DIST the ball is genuinely gone, not a knock — the
    // gather releases it (a stale owner cannot retrieve a ball from range).
    if (gdist > DRIBBLE_GATHER_START && gdist < DRIBBLE_GATHER_MAX_DIST && gRelSpeed < DRIBBLE_GATHER_MAX_REL_SPEED) {
      const g0x = b.vx, g0y = b.vy, g0z = b.vz;
      if (b.possessedBy === id) {
        // ── STILL POSSESSED (ball ahead of the feet): bleed the recession so a
        //    carry cannot ride past the envelope. Velocity only, never toward the
        //    carrier, so a normal knock-and-run keeps the ball ahead and is not
        //    glued — and it is never stranded behind on a 180 (see dribble-follow).
        const awayx = gdx / gdist;
        const awayz = gdz / gdist;
        // RADIAL — bleed the receding component (ball velocity relative to the
        // carrier projected on `away`), 0 at the gather start to full at the
        // boundary. Relative-to-carrier is correct on a TURN (his velocity is not
        // along `away`), unlike subtracting raw speed.
        const awayVel = (b.vx - p.vx[id]!) * awayx + (b.vz - p.vz[id]!) * awayz;
        // NOTE — do NOT "improve" this by releasing the check for a ball BEHIND
        // him. Tried and measured: it makes things worse, because this check is
        // also what BOUNDS the separation while the OWNERSHIP envelope
        // (POSSESSION_CONTROL_RADIUS 2.35 m, far wider than the boot) is what
        // actually keeps the ball his. Direction-gating it left the ball trailing
        // at 2.3 m instead of 1.4 m, pushed the 180° reversal to 2.26 m behind and
        // broke two acceptance bounds (§1 envelope S-curve 2.41 m > 2.3; corridor
        // owned-perp 1.28-1.90 m > 1.25). The tow is cured at its cause instead:
        // the knock now corrects the ball's LINE as well as its pace (below), so a
        // curve keeps the ball on the boot, and a carrier who has run off his ball
        // can no longer hold pace (carryHasBrokenDown, player-controller.ts).
        if (awayVel > 0) {
          const tRad = clamp01((gdist - DRIBBLE_GATHER_START) / (DRIBBLE_GATHER_RADIUS - DRIBBLE_GATHER_START));
          const bRad = DRIBBLE_GATHER_STRENGTH * tRad;
          b.vx -= awayx * awayVel * bRad;
          b.vz -= awayz * awayVel * bRad;
        }
        // ── THE TURN SHEPHERD: THE BALL FOLLOWS HIM AROUND ────────────────
        // The radial bleed above answers a ball RUNNING AWAY. It has nothing to
        // say about a ball that is merely on the OLD LINE while the man is on a
        // new one, which is what a turn produces: measured, a sustained turn
        // put the ball 1.9 m out at 165 deg - directly behind him - still
        // owned (owner: "it should always be in front of him"). His foot now
        // steers the ball's LINE toward his heading, proportional to how far
        // off it has drifted, capped per tick and applied to velocity only.
        // Nothing here can hold a ball a defender has taken: the whole gather
        // is dead once he is not the owner.
        const shepDir = Math.hypot(p.vx[id]!, p.vz[id]!) > 0.5
          ? datan2(p.vz[id]!, p.vx[id]!)
          : p.yaw[id]!;
        const ballDir = datan2(gdz, gdx);
        const offDeg = Math.abs(angleDelta(shepDir, ballDir)) * (180 / Math.PI);
        // ⚠ NOT WHILE HE IS STRIKING IT. A committed action (pass, shot,
        // first-time hit) has its own contact geometry, and steering the ball
        // sideways underneath it moves the ball off the boot: measured, the
        // pass-quality harness's first-time cell stopped firing entirely.
        // Shepherding is for a ball he is CARRYING, not one he is hitting.
        // ...and ONLY A BALL HE IS ACTUALLY DRIBBLING. A dribbled ball travels
        // WITH him (relative speed 1-2 m/s at a sprint); a ball arriving to be
        // struck first-time does not (7 m/s in the pass-quality harness, whose
        // first-time cell went 3/3 -> 0/3 when the shepherd steered it off the
        // boot before the strike could commit). Relative speed is the honest
        // difference between the two.
        // ⚠ AND NOT ON THE TICK HE ASKS. The committed-action gate above is not
        // enough: the request and the commit are the same tick, and the
        // shepherd runs first. ISOLATED by zeroing the impulse - the harness's
        // first-time cell went straight back to 3/3 - so the strike was being
        // steered off the boot by a touch that had not yet been told a strike
        // was coming. If he is asking to hit it, hands off.
        const asking = strikeRequested(state, id, input);
        const shepRel = Math.hypot(b.vx - p.vx[id]!, b.vz - p.vz[id]!);
        const shepherding = !asking
          && p.actKind[id]! === ActionKind.None
          && shepRel < DRIBBLE_TURN_SHEPHERD_MAX_REL
          && offDeg > DRIBBLE_TURN_SHEPHERD_TOL_DEG;
        if (shepherding) {
          // PURELY TANGENTIAL — the touch SWINGS the ball around him, it never
          // pulls it in. Aiming a correction at a control point had an inward
          // component that drove the ball through his own boots (measured:
          // 11.5 cm of interpenetration on a jog-stop, bound 5 cm), and
          // stripping that component afterwards gutted the correction (80 deg
          // still off line). The honest quantity is the ORBIT speed: to close
          // an angular error `err` at radius `gdist` inside RECOVER_S the ball
          // needs gdist*err/RECOVER_S sideways, so ask for exactly that and
          // cap the change. Distance stays the radial bleed's job.
          const errRad = angleDelta(shepDir, ballDir);   // signed
          const sgn = errRad > 0 ? -1 : 1;               // swing back toward his line
          const perpx = -awayz * sgn;
          const perpz = awayx * sgn;
          const wantTan = (gdist * Math.abs(errRad)) / DRIBBLE_TURN_RECOVER_S;
          const haveTan = (b.vx - p.vx[id]!) * perpx + (b.vz - p.vz[id]!) * perpz;
          let dTan = wantTan - haveTan;
          const maxDv = DRIBBLE_TURN_SHEPHERD_DV * FIXED_DT;
          if (dTan > maxDv) dTan = maxDv;
          if (dTan < 0) dTan = 0;                        // never brake his touch
          b.vx += perpx * dTan;
          b.vz += perpz * dTan;
        }
        // TANGENTIAL (orbit) — ONLY in the escape margin past the envelope. A ball
        // circling the edge at pace has ~no radial recession to catch, so it can
        // drift out sideways and slingshot away on a hard cut; bleed its sideways
        // component too, ramping across the margin. A normal in-envelope carry
        // (gdist ≤ RADIUS) never reaches this, so it is untouched.
        if (gdist > DRIBBLE_GATHER_RADIUS) {
          const awayVel2 = (b.vx - p.vx[id]!) * awayx + (b.vz - p.vz[id]!) * awayz;
          const tanx = (b.vx - p.vx[id]!) - awayVel2 * awayx;
          const tanz = (b.vz - p.vz[id]!) - awayVel2 * awayz;
          const tTan = clamp01((gdist - DRIBBLE_GATHER_RADIUS) / (DRIBBLE_GATHER_MAX_DIST - DRIBBLE_GATHER_RADIUS));
          const bTan = DRIBBLE_GATHER_STRENGTH * tTan;
          b.vx -= tanx * bTan;
          b.vz -= tanz * bTan;
        }
      } else {
        // ── LOOSE but still OWNED (a knock ran just past the capture gate): a bare
        //    pace-match would freeze the ball at a fixed gap the carrier never
        //    closes (he has turned; he will not run back onto it). Instead a capped
        //    REACHING touch toward a control point a step ahead of him draws the
        //    ball back onto the running line, so the next touch re-collects it — a
        //    touch, not glue (per-tick DV cap), and the position is never set.
        const pvx = p.vx[id]!;
        const pvz = p.vz[id]!;
        const pspeed = Math.sqrt(pvx * pvx + pvz * pvz);
        let ldx: number;
        let ldz: number;
        if (pspeed > 1e-3) { ldx = pvx / pspeed; ldz = pvz / pspeed; }
        else { ldx = dcos(p.yaw[id]!); ldz = dsin(p.yaw[id]!); }
        const targetX = px + ldx * DRIBBLE_GATHER_LEAD;
        const targetZ = pz + ldz * DRIBBLE_GATHER_LEAD;
        // Want-velocity = carry with the player + a spring drawing the ball onto
        // the control point. Capped per tick so a big correction is paid out over
        // a few touches (never a teleport) — it draws the ball back onto the
        // running line so the next touch re-collects it, on a cut or a reversal.
        const wantVx = pvx + (targetX - b.px) * DRIBBLE_GATHER_CLOSE_GAIN;
        const wantVz = pvz + (targetZ - b.pz) * DRIBBLE_GATHER_CLOSE_GAIN;
        const dvx = wantVx - b.vx;
        const dvz = wantVz - b.vz;
        const dvMag = Math.sqrt(dvx * dvx + dvz * dvz);
        const maxDv = DRIBBLE_GATHER_CLOSE_MAX_DV * FIXED_DT;
        const k = dvMag <= maxDv ? 1 : maxDv / dvMag;
        b.vx += dvx * k;
        b.vz += dvz * k;
      }
      traceBallSince(state, BallVelocityCause.FootTouch, id, g0x, g0y, g0z);
    }
  }

  // Everything below — the knock-and-run and the shepherding spring — is a touch
  // that DRIVES the ball (ahead in stride, or onto the lead point). It may run
  // ONLY while this player actually possesses the ball. When he merely OWNS a
  // loose ball (the gather grace above), the gather has already acted to bring it
  // back; knocking it ahead again would fling a ball he is chasing further away
  // (the live "loose ball gets booted downfield by its own dribbler" defect).
  if (b.possessedBy !== id) return;

  // A dribble touch is a FOOT touch: it fires ONLY when the ball is inside the
  // boot's reach — the same physical gate every footed action obeys (contact
  // truth). Beyond reach the ball is running away from the feet: no touch, chase
  // it. This works because the lead point below is clamped inside reach, so a
  // controlled carry keeps the ball in range and a real touch shepherds it —
  // rather than a tractor beam yanking it back from a full stride away. (§3.4)
  if (!inFootReach(px, pz, p.py[id]!, b.px, b.py, b.pz)) return;

  // Lead direction: where the player is running. When stopped, hold the ball on
  // its OWN bearing rather than the facing — a stationary carrier who swings the
  // aim around must not drag the ball orbiting after the cursor; the ball just
  // sits at the feet, clear of the capsule. (Only the movement branch leads it.)
  const pvx = p.vx[id]!;
  const pvz = p.vz[id]!;
  const pspeed = Math.sqrt(pvx * pvx + pvz * pvz);
  let dirx: number;
  let dirz: number;
  if (pspeed > 1e-3) {
    dirx = pvx / pspeed;
    dirz = pvz / pspeed;
    // PREDICTIVE steer (polish §6): a touch is aimed where the carrier WANTS to
    // go, not only where his momentum still points. Velocity lags a turn by the
    // turn-rate curve, so a touch along raw velocity sends the ball up the OLD
    // running line — the live "ball escapes the path on a turn" defect. Blend
    // toward the carrier's own requested heading; momentum keeps a share, so a
    // sprint turn still exposes the ball (the §3.3 cost stays real). AI and
    // remote players steer identically — it is their input too.
    if (input !== null) {
      const mx = input.moveX;
      const mz = input.moveZ;
      const mlen = Math.sqrt(mx * mx + mz * mz);
      if (mlen > 0.15) {
        const bx = dirx * (1 - DRIBBLE_STEER_WEIGHT) + (mx / mlen) * DRIBBLE_STEER_WEIGHT;
        const bz = dirz * (1 - DRIBBLE_STEER_WEIGHT) + (mz / mlen) * DRIBBLE_STEER_WEIGHT;
        const blen = Math.sqrt(bx * bx + bz * bz);
        if (blen > 1e-6) {
          dirx = bx / blen;
          dirz = bz / blen;
        }
      }
    }
  } else {
    const bdx = b.px - px;
    const bdz = b.pz - pz;
    const blen = Math.sqrt(bdx * bdx + bdz * bdz);
    if (blen > 1e-3) {
      dirx = bdx / blen;
      dirz = bdz / blen;
    } else {
      dirx = dcos(p.yaw[id]!);
      dirz = dsin(p.yaw[id]!);
    }
  }

  const dribbleMul = statMultiplier(state.attributes[id]!.dribbling);

  // Which boot the ball is wanted on THIS tick, as a signed offset across the
  // running line. Every target below that used to sit on the centreline now
  // sits `weaveLat` to one side of it, and every test that used to measure
  // deviation FROM the centreline now measures it from that point. The
  // correction keeping the ball there is unchanged in strength — it just aims
  // at a foot. Below DRIBBLE_WEAVE_MIN_SPEED this is exactly 0 and every line
  // below is arithmetically identical to what it was.
  const weaveLat = dribbleWeaveLat(state, id, pspeed);
  /** Left-perpendicular to `dir`, i.e. the axis `weaveLat` is measured along. */
  const perpX = -dirz;
  const perpZ = dirx;

  // ── Knock-and-run: at pace, discrete touches replace the shepherding spring ──
  // A spring cannot track an accelerating sprinter (measured: he overruns the
  // ball, the capsule swallows it, the spring flings it to 9+ m/s). What a real
  // player does at pace is KNOCK the ball a touch ahead and run after it. The
  // knock fires only when the boot is on the ball (in reach, gated above) and the
  // ball has stopped running ahead; between knocks the ball is pure physics.
  // Better dribblers knock softer — a tighter carry. (§3.4, defects #3/#4)
  if (pspeed >= DRIBBLE_KNOCK_MIN_SPEED) {
    const fwd = (b.px - px) * dirx + (b.pz - pz) * dirz;
    if (fwd < 0) return; // overran it — no touch backwards through your own body
    // How far the ball sits OFF the running line (signed, +left of `dir`). The
    // knock must correct this, not just the pace — see below.
    const lat = (b.px - px) * -dirz + (b.pz - pz) * dirx;
    const relFwd = b.vx * dirx + b.vz * dirz - pspeed;
    // Knock when the ball has stopped running ahead, OR is about to be swallowed
    // by the capsule (proximity — a sweep-pinned ball reads a big velocity while
    // going nowhere, so geometry, not velocity, is the trustworthy trigger), OR
    // has drifted OFF THE LINE. That last clause is the fix for the live defect
    // "the ball leaves the feet and ends up behind him": on a curve the ball is
    // still running ahead fast (relFwd stays high), so the pace triggers never
    // fired, and the ball rode out sideways, left the boot's reach for good and
    // was then dragged along behind the carrier by the gather's pace-match. A
    // carry at pace is a SEQUENCE of touches that keep the ball on the line — a
    // ball running ahead but WIDE needs a touch just as much as a slow one.
    if (
      relFwd > DRIBBLE_KNOCK_TRIGGER_REL &&
      fwd > DRIBBLE_KNOCK_PROX &&
      (lat - weaveLat < DRIBBLE_KNOCK_LINE_TOL && lat - weaveLat > -DRIBBLE_KNOCK_LINE_TOL)
    ) return;
    const knock = pspeed + DRIBBLE_KNOCK_REL_SPEED / dribbleMul;
    // The knock is forward pace PLUS a bounded ACROSS component that walks the
    // ball back onto the running line. The old knock assigned the velocity
    // straight down `dir` from wherever the ball happened to be, so it corrected
    // the ball's PACE but never its BEARING: whatever lateral error the ball
    // picked up between touches was preserved by every later touch, and on a
    // sustained turn it accumulated until the ball sat outside foot reach for
    // good. Sweeping the ball back across is also what a dribbler physically
    // does with the inside of the boot.
    //
    // Sized to null the offset over about one knock cycle
    // (DRIBBLE_KNOCK_LINE_RECOVER_S) and hard-clamped, because an unbounded
    // proportional correction at knock speed OVERSHOOTS the line between touches
    // and oscillates itself apart (measured: the ball crossed the line, the next
    // touch over-corrected the other way, and it diverged to 0.9 m off-line on a
    // dead-straight sprint). Bounded + re-evaluated every touch is a stable
    // first-order return.
    let across = (weaveLat - lat) / DRIBBLE_KNOCK_LINE_RECOVER_S;
    if (across > DRIBBLE_KNOCK_LINE_RECOVER_MAX) across = DRIBBLE_KNOCK_LINE_RECOVER_MAX;
    else if (across < -DRIBBLE_KNOCK_LINE_RECOVER_MAX) across = -DRIBBLE_KNOCK_LINE_RECOVER_MAX;
    const kvx0 = b.vx, kvy0 = b.vy, kvz0 = b.vz;
    b.vx = dirx * knock + -dirz * across;
    b.vz = dirz * knock + dirx * across;
    traceBallSince(state, BallVelocityCause.FootTouch, id, kvx0, kvy0, kvz0);
    if (contactLogOn()) {
      logContact({
        tick: state.tick,
        kind: 'dribble',
        player: id,
        action: 'knock',
        contact: footContact(px, pz, p.py[id]!, b.px, b.py, b.pz),
        ballBefore: [kvx0, kvy0, kvz0],
        ballAfter: [b.vx, b.vy, b.vz],
        deltaV: Math.sqrt((b.vx - kvx0) * (b.vx - kvx0) + (b.vz - kvz0) * (b.vz - kvz0)),
        possessed: b.possessedBy === id,
      });
    }
    return;
  }

  // The lead point the ball wants to sit at: ahead of the feet in the movement
  // direction. Further out at pace (a heavier touch = less control, §3.4),
  // tighter with Dribbling.
  // Speed-aware: scale the lead across the FULL sprint-dribble range, so a
  // knock-and-run rides the ball further ahead than a jog. Clamped to
  // DRIBBLE_LEAD_MAX so even at a sprint the ball stays inside foot reach — the
  // touch above only fires in reach, so an un-clamped lead would let the ball
  // settle out of reach and never get shepherded (the old turn-loss bug).
  const speedFrac = clamp01(pspeed / MAX_SPEED_SPRINT_DRIBBLE);
  let lead = Math.min(
    (DRIBBLE_LEAD_DISTANCE * (1 + DRIBBLE_TOUCH_SPEED_FACTOR * speedFrac)) / dribbleMul,
    DRIBBLE_LEAD_MAX,
  );
  // CLOSE CONTROL (Ctrl) — the ball is kept nearer the boot. The speed cap in
  // player-controller is the other half; a shorter lead at a lower pace is
  // what "takes the ball with dribbling" actually means, and it is what makes
  // the ball harder to nick off him.
  if ((input?.flags ?? 0) & InputFlags.CloseControl) lead *= CLOSE_CONTROL_LEAD_MULT;
  // Is the ball still on the running line? Everything below branches on this:
  // on the line he gets a real touch cadence, off it he gets the tight spring
  // that holds a corner. See DRIBBLE_SETTLE_RADIUS_RUN.
  const settleX = px + dirx * lead + perpX * weaveLat;
  const settleZ = pz + dirz * lead + perpZ * weaveLat;
  const offLine = (settleX - b.px) * -dirz + (settleZ - b.pz) * dirx;
  const onLine = offLine < DRIBBLE_SETTLE_LINE_TOL && offLine > -DRIBBLE_SETTLE_LINE_TOL;
  const alongTol = onLine ? DRIBBLE_SETTLE_RADIUS_RUN : DRIBBLE_SETTLE_RADIUS;

  // A touch OVERSHOOTS the lead point, so the ball has somewhere to roll back
  // from. Off the line there is no overshoot: that is the corner case, and it
  // wants the ball pulled straight onto the lead point exactly as before.
  const push = onLine ? DRIBBLE_TOUCH_PUSH : 0;
  const targetX = px + dirx * (lead + push) + perpX * weaveLat;
  const targetZ = pz + dirz * (lead + push) + perpZ * weaveLat;

  // Under control means BOTH position AND velocity: sitting at the lead point
  // AND travelling with the carrier — then roll free, no touch. Position alone is
  // not control: a ball CROSSING the lead zone 2+ m/s faster than the carrier is
  // escaping, and the old position-only check went hands-off exactly then
  // (measured: sprint-dribble flung the ball to 9.4 m/s through the "settled"
  // window untouched). A hot ball near the feet instead gets the cushioning
  // branch below — the literal first touch. (§3.4)
  const errX = targetX - b.px;
  const errZ = targetZ - b.pz;
  const relVx = b.vx - dirx * pspeed;
  const relVz = b.vz - dirz * pspeed;
  // "Settled" is judged from the LEAD point, not from where the touch aims, and
  // it is judged along and across the running line separately. The along-band is
  // the gap between touches — the ball rolling free with no force on it, which
  // is the dribble — and it is only wide while the ball is on the line.
  const alongErr = (settleX - b.px) * dirx + (settleZ - b.pz) * dirz;
  if (
    alongErr * alongErr <= alongTol * alongTol &&
    offLine * offLine <= DRIBBLE_SETTLE_LINE_TOL * DRIBBLE_SETTLE_LINE_TOL &&
    relVx * relVx + relVz * relVz <= DRIBBLE_SETTLE_REL_SPEED * DRIBBLE_SETTLE_REL_SPEED
  ) {
    return;
  }

  // A DAMPED recapture toward the lead point (a PD controller, not a bare spring).
  // The position error pulls the ball onto the lead; the ball's velocity RELATIVE
  // to the carrier DAMPS that pull, so the ball settles ONTO the lead rather than
  // overshooting past it. The old bare-P controller was clamped to a fixed rel
  // speed for the whole catch-up, so at a sprint it drove the ball ~2.5 m/s over
  // the carrier the entire way — flinging it to ~9.5 m/s, out of reach and gone
  // (possession collapsed to ~43% on a straight sprint-dribble). The damping term
  // is what makes a sprint-dribble stable. Still clamped to DRIBBLE_MAX_REL_SPEED
  // so a single correction never breaches the capture gate. (§3.4)
  let springX = errX * DRIBBLE_RECAPTURE_GAIN - relVx * DRIBBLE_RECAPTURE_DAMP;
  let springZ = errZ * DRIBBLE_RECAPTURE_GAIN - relVz * DRIBBLE_RECAPTURE_DAMP;
  const springMag = Math.sqrt(springX * springX + springZ * springZ);
  if (springMag > DRIBBLE_MAX_REL_SPEED) {
    const s = DRIBBLE_MAX_REL_SPEED / springMag;
    springX *= s;
    springZ *= s;
  }

  // Carry the ball WITH the player (his pace) plus the damped, clamped correction.
  const wantVx = dirx * pspeed + springX;
  const wantVz = dirz * pspeed + springZ;

  // Accelerate toward it rather than assigning it. The per-tick cap is what makes
  // this a touch instead of a tractor beam: a big correction is paid out over a
  // few ticks of contact, and the instant the ball rolls past DRIBBLE_REACH it
  // keeps whatever velocity it had and runs free. (§3.4 — impulses, never glue.)
  const dvx = wantVx - b.vx;
  const dvz = wantVz - b.vz;
  const dvMag = Math.sqrt(dvx * dvx + dvz * dvz);
  if (dvMag < 1e-6) return;

  const maxDv = DRIBBLE_TOUCH_MAX_DV * FIXED_DT;
  const k = dvMag <= maxDv ? 1 : maxDv / dvMag;
  const dvx0 = b.vx, dvy0 = b.vy, dvz0 = b.vz;
  b.vx += dvx * k;
  b.vz += dvz * k;
  traceBallSince(state, BallVelocityCause.FootTouch, id, dvx0, dvy0, dvz0);
  if (contactLogOn()) {
    logContact({
      tick: state.tick,
      kind: 'dribble',
      player: id,
      action: 'dribble',
      contact: footContact(px, pz, p.py[id]!, b.px, b.py, b.pz),
      ballBefore: [dvx0, dvy0, dvz0],
      ballAfter: [b.vx, b.vy, b.vz],
      deltaV: Math.sqrt((b.vx - dvx0) * (b.vx - dvx0) + (b.vz - dvz0) * (b.vz - dvz0)),
      possessed: b.possessedBy === id,
    });
  }
}

/**
 * Drag coefficient as a function of speed — the drag crisis (§3.2). Cd DIPS from
 * the laminar 0.47 to the turbulent 0.20 across the transition band and rises
 * slightly again at the highest speeds. This dip is why a knuckleball dips
 * unpredictably and why a struck free-kick behaves the way it does; a flat Cd
 * kills the entire feel of a long ball. Exported so the drag-crisis test can
 * assert the dip directly.
 */
export function ballDragCoefficient(speed: number): number {
  if (speed < DRAG_LAMINAR_MAX_SPEED) return CD_LAMINAR; // laminar
  if (speed < DRAG_CRISIS_MAX_SPEED) {
    // Transition: lerp 0.47 → 0.20 across the drag crisis.
    const t = (speed - DRAG_LAMINAR_MAX_SPEED) / (DRAG_CRISIS_MAX_SPEED - DRAG_LAMINAR_MAX_SPEED);
    return CD_LAMINAR + (CD_TURBULENT - CD_LAMINAR) * t;
  }
  if (speed < DRAG_TURBULENT_MAX_SPEED) return CD_TURBULENT; // supercritical / turbulent
  return CD_HIGH_SPEED; // slight rise at the top end
}

/**
 * Gravity + quadratic drag (with the drag crisis) + Magnus lift, integrated into
 * the ball's velocity for this tick. (§3.2)
 *
 *   F_drag  = −½·ρ·Cd·A·|v|·v
 *   F_magnus = S·(ω × v),  S = ½·ρ·A·r·Cl,  Cl = clamp(0.385·(r|ω|/|v|)^¼, 0, 0.35)
 *
 * The ^¼ uses dpow025 (= sqrt(sqrt)), which is bit-identical on every engine —
 * the one transcendental in the ball model we get for free (DECISIONS.md D2).
 */
function integrateAero(state: WorldState): void {
  const b = state.ball;
  const rho = state.config.airDensity;

  const speedSq = b.vx * b.vx + b.vy * b.vy + b.vz * b.vz;
  const speed = Math.sqrt(speedSq);

  // Gravity always.
  let ax = 0;
  let ay = GRAVITY;
  let az = 0;

  // Component accels, captured for the ball trace (§3.2). Arithmetic is
  // unchanged: `ax += (−dragK·vx)` is exactly `ax −= dragK·vx` in IEEE-754.
  let dragAx = 0, dragAy = 0, dragAz = 0;
  let magAx = 0, magAy = 0, magAz = 0;

  if (speed > 1e-6) {
    // Drag: a = F/m = −(½·ρ·Cd·A·|v|/m)·v.
    const cd = ballDragCoefficient(speed);
    const dragK = (0.5 * rho * cd * BALL_AREA * speed) / BALL_MASS;
    dragAx = -dragK * b.vx;
    dragAy = -dragK * b.vy;
    dragAz = -dragK * b.vz;
    ax += dragAx;
    ay += dragAy;
    az += dragAz;

    // Magnus: needs a spin.
    const wSq = b.wx * b.wx + b.wy * b.wy + b.wz * b.wz;
    if (wSq > 1e-9) {
      const wMag = Math.sqrt(wSq);
      const spinParam = (BALL_RADIUS * wMag) / speed;
      const cl = clamp(MAGNUS_CL_COEFF * dpow025(spinParam), 0, MAGNUS_CL_MAX);
      if (cl > 0) {
        const s = (0.5 * rho * BALL_AREA * BALL_RADIUS * cl) / BALL_MASS;
        // (ω × v)
        const cx = b.wy * b.vz - b.wz * b.vy;
        const cy = b.wz * b.vx - b.wx * b.vz;
        const cz = b.wx * b.vy - b.wy * b.vx;
        magAx = s * cx;
        magAy = s * cy;
        magAz = s * cz;
        ax += magAx;
        ay += magAy;
        az += magAz;
      }
    }
  }

  b.vx += ax * FIXED_DT;
  b.vy += ay * FIXED_DT;
  b.vz += az * FIXED_DT;

  // Trace: attribute this tick's aero delta to its three physical components.
  if (ballTraceOn()) {
    traceBallDv(state, BallVelocityCause.Gravity, -1, 0, GRAVITY * FIXED_DT, 0);
    if (dragAx !== 0 || dragAy !== 0 || dragAz !== 0) {
      traceBallDv(state, BallVelocityCause.AerodynamicDrag, -1, dragAx * FIXED_DT, dragAy * FIXED_DT, dragAz * FIXED_DT);
    }
    if (magAx !== 0 || magAy !== 0 || magAz !== 0) {
      traceBallDv(state, BallVelocityCause.Magnus, -1, magAx * FIXED_DT, magAy * FIXED_DT, magAz * FIXED_DT);
    }
  }
}

/**
 * Reflect the ball off a surface: restitution on the normal component, and a
 * tangential friction impulse that couples spin ↔ linear velocity — implemented
 * properly, not faked (§3.2 pt 5). This coupling is what makes a backspun ball
 * check up or come back: at contact its underside is racing forward, friction
 * bites, and the reaction torque bleeds the backspin while braking (or reversing)
 * the roll.
 */
function resolveSurface(
  state: WorldState,
  nx: number, ny: number, nz: number,
  kindTag: number,
): void {
  const b = state.ball;

  // The pitch slab is a FLAT plane at y=0, so a Ground contact's true normal is
  // exactly (0,1,0) — always. Rapier reports a DEGENERATE position-dependent
  // normal for a ball flush on the slab (EPA at zero separation), from both the
  // shape-cast and the post-step contact solver; reflecting off it is the phantom
  // ricochet. Pin the slab's real normal. ContactKind.Ground is ONLY the slab —
  // the world-edge walls are ContactKind.Wall and keep their genuine horizontal
  // normals, as do posts, crossbar, net and players. (§3.4)
  if (kindTag === ContactKind.Ground) { nx = 0; ny = 1; nz = 0; }

  const svx0 = b.vx, svy0 = b.vy, svz0 = b.vz;

  const vn = b.vx * nx + b.vy * ny + b.vz * nz;
  const groundLike = ny > 0.7;

  // Restitution by surface.
  let e: number;
  if (kindTag === ContactKind.Post || kindTag === ContactKind.Crossbar) {
    e = BALL_RESTITUTION_POST;
  } else if (kindTag === ContactKind.Net) {
    e = 0; // nets kill pace
  } else {
    e = BALL_RESTITUTION_GRASS;
    if (state.config.wetPitch) e *= WET_RESTITUTION_MULTIPLIER;
  }

  if (vn < 0) {
    // Bounce the normal component: v -= (1+e)·vn·n.
    b.vx -= (1 + e) * vn * nx;
    b.vy -= (1 + e) * vn * ny;
    b.vz -= (1 + e) * vn * nz;

    // Tangential friction + spin coupling on ground-like surfaces (§3.2 pt 5).
    if (groundLike) applyContactFriction(state, nx, ny, nz, -vn, e);

    // Ground rest latch: a ball no longer bouncing away rolls from here.
    if (groundLike && (b.vx * nx + b.vy * ny + b.vz * nz) < BALL_GROUND_REST_SPEED) {
      b.grounded = 1;
    }
  } else if (groundLike) {
    // Resting on / skimming the ground without an into-surface velocity.
    b.grounded = 1;
  }

  if (kindTag === ContactKind.Net) {
    b.vx *= NET_VELOCITY_DAMPING;
    b.vy *= NET_VELOCITY_DAMPING;
    b.vz *= NET_VELOCITY_DAMPING;
  }

  traceBallSince(state, surfaceCause(kindTag), -1, svx0, svy0, svz0);
}

/**
 * The tangential friction impulse at a contact, with the proper spin↔linear
 * coupling. Contact point sits at −R·n from the ball centre; the surface velocity
 * there is v + ω×r. Friction opposes the tangential slip, capped by Coulomb
 * (μ·normal impulse), and applies an equal torque back into the spin. (§3.2 pt 5)
 */
function applyContactFriction(
  state: WorldState,
  nx: number, ny: number, nz: number,
  normalSpeed: number,
  e: number,
): void {
  const b = state.ball;
  const r = BALL_RADIUS;
  const rx = -r * nx;
  const ry = -r * ny;
  const rz = -r * nz;

  // Surface velocity at the contact point: v + ω × r.
  const cvx = b.vx + (b.wy * rz - b.wz * ry);
  const cvy = b.vy + (b.wz * rx - b.wx * rz);
  const cvz = b.vz + (b.wx * ry - b.wy * rx);

  // Tangential slip = contact velocity minus its normal component.
  const cvn = cvx * nx + cvy * ny + cvz * nz;
  const tx = cvx - cvn * nx;
  const ty = cvy - cvn * ny;
  const tz = cvz - cvn * nz;
  const tmag = Math.sqrt(tx * tx + ty * ty + tz * tz);
  if (tmag < 1e-6) return;

  const tdx = tx / tmag;
  const tdy = ty / tmag;
  const tdz = tz / tmag;

  // Impulse that would exactly kill the slip, accounting for the ball's rotation:
  //   j_stop = m·|vt| / (1 + m·r²/I).
  const mR2overI = (BALL_MASS * r * r) / BALL_INERTIA;
  const jStop = (BALL_MASS * tmag) / (1 + mR2overI);

  // Coulomb cap: μ · normal impulse. Normal impulse magnitude ≈ m·(1+e)·|vn|.
  let mu = BALL_FRICTION_GRASS;
  if (state.config.wetPitch) mu *= WET_FRICTION_MULTIPLIER;
  const jMax = mu * BALL_MASS * (1 + e) * normalSpeed;

  const j = jStop < jMax ? jStop : jMax;

  // Linear: v -= (j/m)·t̂.
  const jm = j / BALL_MASS;
  b.vx -= jm * tdx;
  b.vy -= jm * tdy;
  b.vz -= jm * tdz;

  // Angular: Δω = (r × impulse)/I, impulse = −j·t̂.
  const impx = -j * tdx;
  const impy = -j * tdy;
  const impz = -j * tdz;
  b.wx += (ry * impz - rz * impy) / BALL_INERTIA;
  b.wy += (rz * impx - rx * impz) / BALL_INERTIA;
  b.wz += (rx * impy - ry * impx) / BALL_INERTIA;
  clampSpin(b);
}

/**
 * A ball settled on the grass: rolling resistance (§3.2 pt 5) and fast ground
 * spin decay. Rolling resistance is F = −0.02·m·g·v̂, so the deceleration is
 * 0.02·|g|, independent of mass.
 */
function applyGroundRoll(state: WorldState): void {
  const b = state.ball;
  const rvx0 = b.vx, rvy0 = b.vy, rvz0 = b.vz;

  const hSpeed = Math.sqrt(b.vx * b.vx + b.vz * b.vz);
  if (hSpeed > 1e-6) {
    const decel = BALL_ROLLING_RESISTANCE * (-GRAVITY) * FIXED_DT;
    const scale = hSpeed > decel ? (hSpeed - decel) / hSpeed : 0;
    b.vx *= scale;
    b.vz *= scale;
  }

  // Settle tiny vertical jitter so a resting ball is truly at rest.
  if (b.vy < BALL_GROUND_REST_SPEED && b.vy > -BALL_GROUND_REST_SPEED) b.vy = 0;

  traceBallSince(state, BallVelocityCause.GroundContact, -1, rvx0, rvy0, rvz0);

  // ⚠ KNOWN DEFECT — THE BALL IS ~30× TOO DRAGGY. See ball-roll-distance.test.ts.
  //
  // Decaying all three spin axes to ZERO is why a ground pass dies after eight
  // metres. `applyContactFriction` is Coulomb friction on the SLIP at the contact
  // point (`v + ω×r`); a ball rolling without slipping has zero slip there, so
  // that function returns early and costs the ball nothing — which is correct,
  // and is why real footballs run so far. But decaying ω toward zero every tick
  // DESTROYS the rolling condition: slip reappears, full kinetic friction fires
  // again next tick, and the ball is left permanently SKIDDING. Measured: it
  // sheds pace at ~6 m/s², 29.9× what BALL_ROLLING_RESISTANCE (0.02·g ≈
  // 0.2 m/s²) — and this function's own header — claims.
  //
  // THE FIX IS KNOWN AND WAS MEASURED. Relax the two horizontal axes toward the
  // ROLLING spin instead of toward zero (flat ground, n = +Y, r = −R·n ⇒ the
  // contact point is stationary when `wx = vz/R` and `wz = −vx/R`), keeping the
  // plain decay only on `wy`, which contributes no rolling and genuinely scrubs
  // off. That took a 10 m/s pass from **8.5 m to 42.8 m** and deceleration from
  // 5.86 to 1.17 m/s².
  //
  // IT IS NOT APPLIED HERE, deliberately. It is a foundational change, not a
  // tuning value: it broke 12 tests across 8 files, including `ai/ball-travel.ts`
  // — the AI's own two-branch fit of the deceleration curve, which is calibrated
  // to THIS defect and whose test literally asserts "a 10 m/s ground pass really
  // does die inside 10 m". Landing the physics without re-deriving that model and
  // re-tuning pass power, the dribble knock, curve and collision would make
  // passing WORSE, not better.
  //
  // Whoever picks this up: fix the physics first, then re-fit `groundDecel(v)`
  // against a stepped ball, then re-tune. The probe test prints the numbers.
  const groundDecay = 1 - BALL_SPIN_DECAY_GROUND * FIXED_DT;
  const gd = groundDecay < 0 ? 0 : groundDecay;
  b.wx *= gd; b.wy *= gd; b.wz *= gd;
}

/**
 * Integrate the render orientation from the spin (cosmetic, but hashed, so it
 * must be deterministic). Small-angle: q += ½·(ω_quat ⊗ q)·dt, then renormalize.
 */
function integrateSpinQuat(state: WorldState): void {
  const b = state.ball;
  const wSq = b.wx * b.wx + b.wy * b.wy + b.wz * b.wz;
  if (wSq < 1e-9) return;

  const half = 0.5 * FIXED_DT;
  const dqx = half * (b.wx * b.qw + b.wy * b.qz - b.wz * b.qy);
  const dqy = half * (-b.wx * b.qz + b.wy * b.qw + b.wz * b.qx);
  const dqz = half * (b.wx * b.qy - b.wy * b.qx + b.wz * b.qw);
  const dqw = half * (-b.wx * b.qx - b.wy * b.qy - b.wz * b.qz);

  let qx = b.qx + dqx;
  let qy = b.qy + dqy;
  let qz = b.qz + dqz;
  let qw = b.qw + dqw;
  const len = Math.sqrt(qx * qx + qy * qy + qz * qz + qw * qw);
  if (len > 1e-9) {
    const inv = 1 / len;
    qx *= inv; qy *= inv; qz *= inv; qw *= inv;
  }
  b.qx = qx; b.qy = qy; b.qz = qz; b.qw = qw;
}

/** Scalar lerp, built from safe IEEE ops only. */
function lerpN(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
